import { serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, startOfDay, isBefore, startOfHour } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { enUS } from "date-fns/locale";
import { toast } from "sonner";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Upload, X, Zap } from "lucide-react";
import { PollMarkdownContent } from "../../components/polls/poll-markdown-content";
import { useAuth } from "../../app/useAuth";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useFriends } from "../../hooks/useFriends";
import { useQuestingGroups } from "../../hooks/useQuestingGroups";
import { useCalendarNavigation } from "../../hooks/useCalendarNavigation";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useEnrichedCalendarEvents } from "../calendar-enrichment/useEnrichedCalendarEvents";
import { CalendarJumpControls } from "../../components/ui/calendar-jump-controls";
import { useUserProfiles, useUserProfilesByIds } from "../../hooks/useUserProfiles";
import { useSchedulerEditorData } from "./hooks/useSchedulerEditorData";
import { useSchedulerEditorEmbeddedPolls } from "./hooks/useSchedulerEditorEmbeddedPolls";
import { APP_URL } from "../../lib/config";
import {
  createEmbeddedBasicPoll,
  deleteEmbeddedBasicPoll,
  notifyEmbeddedBasicPollRequiredChanged,
  reorderEmbeddedBasicPolls,
  updateEmbeddedBasicPoll,
} from "../../lib/data/basicPolls";
import {
  addSchedulerSlot,
  deleteField,
  deleteSchedulerSlot,
  deleteSchedulerVote,
  setScheduler,
  updateScheduler,
  upsertSchedulerSlot,
  upsertSchedulerVote,
} from "../../lib/data/schedulers";
import { resolveIdentifier } from "../../lib/identifiers";
import { createReminderSettingsSnapshot } from "../reminders/reminder-settings";
import {
  PollNotificationSettings,
} from "../reminders/PollNotificationSettings";
import { pollReminderSummary } from "../reminders/poll-notification-settings";
import { AutoNagSettings } from "../reminders/AutoNagSettings";
import {
  createAutoNagSettingsSnapshot,
  createDefaultAutoNagSettings,
  validateAutoNagAuthoring,
} from "../reminders/auto-nag";
import { autoNagSummary } from "../reminders/auto-nag-summary";
import { buildNotificationActor, emitPollEvent } from "../../lib/data/notification-events";
import { sendPendingPollInvites, revokePollInvite } from "../../lib/data/pollInvites";
import { findUserIdsByEmails } from "../../lib/data/users";
import { buildEmailSet, normalizeEmail, normalizeEmailList } from "../../lib/utils";
import { formatZonedDateTime, formatZonedTime, shouldShowTimeZone, toDisplayDate } from "../../lib/time";
import { validateInviteCandidate } from "./utils/invite-utils";
import {
  removeEmbeddedPollDraft,
  reorderEmbeddedPollDrafts,
  toEmbeddedPollCreatePayloads,
  upsertEmbeddedPollDraft,
} from "./utils/embedded-poll-drafts";
import { InvitePanel } from "./components/invite-panel";
import { EmbeddedPollEditorModal } from "./components/EmbeddedPollEditorModal";
import { QuestingGroupSelect } from "./components/questing-group-select";
import { SchedulerFormHeader } from "./components/scheduler-form-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { AvatarStack } from "../../components/ui/voter-avatars";
import { buildColorMap, uniqueUsers } from "../../components/ui/voter-avatar-utils";
import { DatePicker } from "../../components/ui/date-picker";
import { Switch } from "../../components/ui/switch";
import { LoadingState } from "../../components/ui/spinner";
import { ErrorState } from "../../components/ui/error-state";
import { resolveLoadState } from "../../lib/load-state";
import {
  DEFAULT_VOTE_ANONYMIZATION,
  DEFAULT_VOTE_VISIBILITY,
  VOTE_ANONYMIZATION,
  getVoteIdentityDisplayMode,
  resolveVoteAnonymization,
  resolveHideVoterIdentitiesForVisibility,
  resolveVoteVisibility,
} from "../../lib/vote-visibility";
import { consumePollImportToken } from "../../lib/poll-import-token";
import { planQuickAddTap, buildQuickAddSlot, getQuickAddWeekday } from "./utils/quick-add-slots";
import { anonymizeSlotVotersForCreator } from "./utils/voter-display";
import { VotePrivacySettings } from "../../components/polls/vote-privacy-settings";
import { hasSubmittedSchedulerVote, nonCreatorHasVoted } from "../../lib/vote-utils";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./calendar-styles.css";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { CalendarToolbar } from "./components/CalendarToolbar";
import { UploadPollJsonDialog } from "../dashboard/components/upload-poll-json-dialog";
import { IMPORT_MODE } from "../../lib/poll-schema";
import { useProfileInteraction } from "../../hooks/useProfileInteraction";
import { ProfileInteractionModals } from "../../components/ProfileInteractionModals";
import { useIncomingAvailabilityShares } from "../../hooks/useIncomingAvailabilityShares";
import { useSharedAvailabilityOverlays } from "../../hooks/useSharedAvailabilityOverlays";
import { useAvailabilityEvents } from "../../hooks/useAvailabilityEvents";
import { useAvailabilityMarks } from "../../hooks/useAvailabilityMarks";
import { AvailabilityOverlayStrip } from "./components/AvailabilityOverlayStrip";
import { AvailabilityOverlayPicker } from "./components/AvailabilityOverlayPicker";
import { computeOverlayCandidateOwnerIds } from "./utils/overlay-candidates";
import { canonicalTimezoneOptions } from "./utils/timezone-options";
import { expandMarksToCalendarEvents, buildMarkStyleGetter } from "../availability/utils/marks-render";
import { ownerTintStyle, resolveOwnerColor } from "./utils/owner-tint";
import {
  loadColorOverrides,
  setColorOverride,
  clearColorOverride,
} from "./utils/overlay-color-overrides";
import { getUserIdentity } from "../../lib/identity";
import { buildOverlayTooltip } from "./utils/overlay-tooltip";
import { buildBusyOverlayEvent } from "./utils/busy-overlay-events";
import { classifySlotsAgainstOverlays } from "./utils/slot-overlay-classify";
import { SlotOverlayIndicators } from "./components/SlotOverlayIndicators";
import { CreatorSlotAgenda } from "./components/CreatorSlotAgenda";
import { resolveInitialCalendarMonth } from "./utils/initial-calendar-month";
import { resolveSchedulerVotingDeadline } from "../../lib/voting-deadlines";
import {
  toSessionDeadlineLocalValue,
  validateSessionDeadlineChange,
} from "./utils/session-voting-deadline";
import { SessionVotingDeadlineField } from "./components/SessionVotingDeadlineField";

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { "en-US": enUS },
});

const DragAndDropCalendar = withDragAndDrop(Calendar);

function SortableEmbeddedPollCard({
  poll,
  voteCount,
  participantCount,
  onEdit,
  onRemove,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: poll.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };
  const voteTypeLabel =
    poll?.settings?.voteType === "RANKED_CHOICE" ? "Ranked choice" : "Multiple choice";
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {poll.title || "Untitled poll"}
            </span>
            <span className="rounded-full border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:border-slate-600 dark:text-slate-300">
              {voteTypeLabel}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                poll.required
                  ? "border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200"
                  : "border border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              {poll.required ? "Required" : "Optional"}
            </span>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {voteCount}/{participantCount} voted
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-300"
            aria-label={`Drag ${poll.title || "poll"}`}
            {...attributes}
            {...listeners}
          >
            Drag
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded-md border border-rose-300 px-2 py-1 text-xs text-rose-700 transition-colors hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-900/30"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Shape a single availability-overlay candidate descriptor from its profile
 * sources. Pure + exported for unit testing.
 *
 * Privacy (identity & email-privacy initiative): the descriptor must carry the
 * friend's chosen public identifier so downstream renderers (chip / picker /
 * slot indicators) route through the identity helpers. We do NOT bake the raw
 * email into `displayName`; email still rides along on the object (Model Y) so
 * email-type users resolve correctly, but it is never used as a display
 * fallback for a friend who chose a non-email identifier.
 */
// eslint-disable-next-line react-refresh/only-export-components -- intentional: colocated test/util export, not a component
export function shapeOverlayCandidate({ ownerId, fromProjection, fallback } = {}) {
  return {
    ownerId,
    displayName:
      fromProjection?.displayName || fallback?.displayName || null,
    avatarUrl: fromProjection?.avatarUrl || fallback?.photoURL || null,
    email: fallback?.email || null,
    publicIdentifier: fallback?.publicIdentifier || null,
    publicIdentifierType: fallback?.publicIdentifierType || null,
    qsUsername: fallback?.qsUsername || null,
    discordUsername: fallback?.discordUsername || null,
  };
}

export default function CreateSchedulerPage() {
  const { id: editId } = useParams();
  const [searchParams] = useSearchParams();
  const importToken = searchParams.get("importToken");
  const isEditing = Boolean(editId);
  const { user } = useAuth();
  const { settings, timezoneMode, timezone, getSessionDefaults, recentInvitees } = useUserSettings();
  const { friends } = useFriends();
  const { groups, getGroupColor } = useQuestingGroups();
  const navigate = useNavigate();
  // Resolved once per mount (stable for the session) — used to label the "Auto
  // (browser)" timezone option AND to dedupe it out of the canonical timezone
  // list below (the browser TZ is almost always also in Intl.supportedValuesOf,
  // and a duplicate Select `value` triggers a React "two children with the same
  // key" error). canonicalTimezoneOptions dedups on the RESOLVED zone so alias
  // forms (Asia/Calcutta ↔ Asia/Kolkata) are caught on non-V8 engines too.
  const browserTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionTab, setDescriptionTab] = useState("write");
  const [invites, setInvites] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteInput, setInviteInput] = useState("");
  const [slots, setSlots] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("month");
  const [submitting, setSubmitting] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(null);
  const [draftTime, setDraftTime] = useState("18:00");
  const [draftDuration, setDraftDuration] = useState(240);
  const [allowLinkSharing, setAllowLinkSharing] = useState(false);
  const [voteVisibility, setVoteVisibility] = useState(DEFAULT_VOTE_VISIBILITY);
  const [voteAnonymization, setVoteAnonymization] = useState(DEFAULT_VOTE_ANONYMIZATION);
  const [hideVoterIdentities, setHideVoterIdentities] = useState(false);
  const [advancedSettingsExpanded, setAdvancedSettingsExpanded] = useState(false);
  const [reminderSettings, setReminderSettings] = useState(() =>
    createReminderSettingsSnapshot(undefined)
  );
  const [reminderSettingsGroupId, setReminderSettingsGroupId] = useState(null);
  const [autoNag, setAutoNag] = useState(() =>
    createDefaultAutoNagSettings({ timeZone: browserTimezone })
  );
  const [calendarSyncEnabled, setCalendarSyncEnabled] = useState(true);
  const [votingDeadlineLocal, setVotingDeadlineLocal] = useState("");
  const [initialVotingDeadlineLocal, setInitialVotingDeadlineLocal] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [timezoneInitialized, setTimezoneInitialized] = useState(false);
  const [loadedFromPoll, setLoadedFromPoll] = useState(false);
  const [loadedFromImport, setLoadedFromImport] = useState(false);
  const [initialSlotIds, setInitialSlotIds] = useState(new Set());
  const [initialSlotTimes, setInitialSlotTimes] = useState({});
  const [calendarUpdateOpen, setCalendarUpdateOpen] = useState(false);
  const [calendarUpdateChecked, setCalendarUpdateChecked] = useState(false);
  const [embeddedPollEditorOpen, setEmbeddedPollEditorOpen] = useState(false);
  const [editingEmbeddedPoll, setEditingEmbeddedPoll] = useState(null);
  const [embeddedPollSaveBusy, setEmbeddedPollSaveBusy] = useState(false);
  const [deleteEmbeddedPollOpen, setDeleteEmbeddedPollOpen] = useState(false);
  const [embeddedPollToDelete, setEmbeddedPollToDelete] = useState(null);
  const [embeddedPollDeleteBusy, setEmbeddedPollDeleteBusy] = useState(false);
  const [draftEmbeddedPolls, setDraftEmbeddedPolls] = useState([]);
  const pollImportEnabled = import.meta.env.VITE_ENABLE_POLL_IMPORT !== "false";
  const [addonImportOpen, setAddonImportOpen] = useState(false);
  const [quickAddMode, setQuickAddMode] = useState(false);
  const [editingSlotId, setEditingSlotId] = useState(null);
  // Mobile-only slot-authoring surface toggle. "agenda" (default) shows a
  // tap-friendly day-grouped list of the creator's slots; "calendar" shows the
  // PR1 horizontally-scrolled react-big-calendar grid. Desktop is untouched
  // (always renders the grid). calendarView (month/week/day) is NOT touched
  // here — switching back to Calendar preserves the prior view selection.
  const isMobile = useIsMobile();
  const [mobileSlotSurface, setMobileSlotSurface] = useState("agenda");

  const { schedulerDocRef, scheduler, slotsSnapshot, votesSnapshot } = useSchedulerEditorData({
    schedulerId: editId,
    isEditing,
  });
  // Once a non-creator has voted under "Anonymous for everyone", the
  // anonymity promise can't be retroactively revoked, so the identity-labels
  // setting is locked. Switching TO all_participants stays allowed. While the
  // votes subcollection is still loading in edit mode we lock conservatively
  // (fail-closed) so a fast save can't slip a weakened value through before the
  // non-creator-vote check has data — the lock relaxes once votes resolve.
  const voteAnonymizationLocked =
    isEditing &&
    resolveVoteAnonymization(voteAnonymization) === VOTE_ANONYMIZATION.ALL_PARTICIPANTS &&
    (votesSnapshot?.loading === true ||
      nonCreatorHasVoted(votesSnapshot?.data, scheduler.data?.creatorId));
  const {
    embeddedPolls,
    setEmbeddedPolls,
    embeddedPollsLoading,
    embeddedPollVoteCounts,
    embeddedPollVoteMeta,
  } = useSchedulerEditorEmbeddedPolls({
    isEditing,
    schedulerId: editId,
  });

  const inviteEmails = useMemo(() => invites, [invites]);
  const explicitParticipantIds = useMemo(
    () => scheduler.data?.participantIds || [],
    [scheduler.data?.participantIds]
  );
  const { profiles: participantProfilesById } = useUserProfilesByIds(explicitParticipantIds);
  const explicitParticipantEmails = useMemo(() => {
    if (!explicitParticipantIds.length) return [];
    return explicitParticipantIds
      .map((id) => participantProfilesById[id]?.email)
      .filter(Boolean)
      .map((email) => normalizeEmail(email));
  }, [explicitParticipantIds, participantProfilesById]);
  const selectedGroup = useMemo(() => {
    if (!selectedGroupId) return null;
    return groups.find((g) => g.id === selectedGroupId) || null;
  }, [selectedGroupId, groups]);
  const inheritedReminderSettings = useMemo(
    () => createReminderSettingsSnapshot(selectedGroup?.reminders, {
      discordLinked: Boolean(selectedGroup?.discord?.channelId),
    }),
    [selectedGroup]
  );
  const earliestProposedSlotAt = useMemo(() => {
    const starts = slots
      .map((slot) => slot?.start instanceof Date ? slot.start : new Date(slot?.start))
      .filter((date) => Number.isFinite(date.getTime()))
      .sort((left, right) => left.getTime() - right.getTime());
    return starts[0] || null;
  }, [slots]);
  const groupMemberIds = useMemo(
    () => selectedGroup?.memberIds || [],
    [selectedGroup?.memberIds]
  );
  const { profiles: groupMemberProfiles } = useUserProfilesByIds(groupMemberIds);
  const groupMemberEmails = useMemo(() => {
    if (!groupMemberIds.length) return [];
    return groupMemberIds
      .map((id) => groupMemberProfiles[id]?.email)
      .filter(Boolean)
      .map((email) => normalizeEmail(email));
  }, [groupMemberIds, groupMemberProfiles]);
  const groupMemberSet = useMemo(() => buildEmailSet(groupMemberEmails), [groupMemberEmails]);
  const profileEmails = useMemo(() => {
    return normalizeEmailList([
      ...invites,
      ...pendingInvites,
      ...groupMemberEmails,
      // Friends power the recommended combobox; fetch their public profiles so
      // each entry resolves to the user's CHOSEN public identifier (display name
      // + Discord/@username/email-by-choice) instead of falling back to email.
      ...friends,
      user?.email,
    ]);
  }, [invites, pendingInvites, groupMemberEmails, friends, user?.email]);
  const { enrichUsers } = useUserProfiles(profileEmails);
  const groupUsers = useMemo(() => {
    if (!groupMemberIds.length) return [];
    const profiles = groupMemberIds.map((id) => groupMemberProfiles[id]).filter(Boolean);
    if (profiles.length > 0) return profiles;
    return enrichUsers(groupMemberEmails);
  }, [groupMemberIds, groupMemberProfiles, enrichUsers, groupMemberEmails]);
  const inviteUsers = useMemo(() => enrichUsers(invites), [enrichUsers, invites]);
  const pendingInviteUsers = useMemo(
    () => enrichUsers(pendingInvites),
    [enrichUsers, pendingInvites]
  );
  const friendSet = useMemo(() => buildEmailSet(friends), [friends]);
  const recommendedEmails = useMemo(() => {
    const userEmail = user?.email ? normalizeEmail(user.email) : null;
    return friends
      .map((email) => normalizeEmail(email))
      .filter(Boolean)
      .filter((email) => email !== userEmail)
      .filter((email) => !invites.includes(email))
      .filter((email) => !pendingInvites.includes(email))
      .filter((email) => !groupMemberSet.has(email));
  }, [friends, invites, pendingInvites, groupMemberSet, user?.email]);
  const recommendedUsers = useMemo(() => enrichUsers(recommendedEmails), [enrichUsers, recommendedEmails]);
  const includedUser = useMemo(() => {
    if (!user?.email) return null;
    const [profile] = enrichUsers([user.email]);
    if (!profile) {
      return { email: user.email, displayName: user.displayName || null };
    }
    if (!profile.displayName && user.displayName) {
      return { ...profile, displayName: user.displayName };
    }
    return profile;
  }, [enrichUsers, user?.email, user?.displayName]);
  const defaultDuration = settings?.defaultDurationMinutes ?? 60;
  const effectiveTimezone = selectedTimezone;
  const votingDeadlineValidation = useMemo(
    () =>
      validateSessionDeadlineChange({
        localValue: votingDeadlineLocal,
        timeZone: effectiveTimezone,
        initialLocalValue: initialVotingDeadlineLocal,
        isEditing,
        slots,
      }),
    [
      votingDeadlineLocal,
      effectiveTimezone,
      initialVotingDeadlineLocal,
      isEditing,
      slots,
    ]
  );
  const showTimeZone = useMemo(() => shouldShowTimeZone(settings), [settings]);
  const slotEvents = useMemo(
    () =>
      slots.map((slot) => ({
        ...slot,
        start: toDisplayDate(
          slot.start instanceof Date ? slot.start : new Date(slot.start),
          effectiveTimezone
        ),
        end: toDisplayDate(
          slot.end instanceof Date ? slot.end : new Date(slot.end),
          effectiveTimezone
        ),
        title: formatZonedTime(slot.start, effectiveTimezone, "h:mm a", { showTimeZone }),
      })),
    [slots, effectiveTimezone, showTimeZone]
  );

  // Phase 4b — friend availability overlay state. activeOverlayOwnerIds is
  // the set of friend uids whose calendars are currently overlaid on the
  // calendar grid. Auto-pruning when an owner leaves the poll's invitees
  // happens in a separate effect (U5).
  const [activeOverlayOwnerIds, setActiveOverlayOwnerIds] = useState([]);
  const { sharesByOwnerId: incomingSharesByOwnerId } =
    useIncomingAvailabilityShares();
  // Candidate set: friends sharing with viewer ∩ proposed poll invitees.
  // The invitee list is a union over four sources so the create flow, edit
  // flow with newly-added pending invitees, and questing-group cascade all
  // surface the right friends. See utils/overlay-candidates.js.
  const inviteeUidsFromEmails = useMemo(() => {
    const emails = [...invites, ...pendingInvites];
    if (!emails.length) return [];
    return enrichUsers(emails)
      .map((u) => u?.uid)
      .filter(Boolean);
  }, [invites, pendingInvites, enrichUsers]);
  const overlayCandidateOwnerIds = useMemo(
    () =>
      computeOverlayCandidateOwnerIds({
        incomingShareOwnerIds: Object.keys(incomingSharesByOwnerId || {}),
        participantIds: explicitParticipantIds,
        groupMemberIds,
        inviteeUidsFromEmails,
        creatorId: scheduler.data?.creatorId || user?.uid || null,
      }),
    [
      incomingSharesByOwnerId,
      explicitParticipantIds,
      groupMemberIds,
      inviteeUidsFromEmails,
      scheduler.data?.creatorId,
      user?.uid,
    ],
  );
  const {
    marks: overlayMarks,
    busyWindows: overlayBusyWindows,
    ownersById: overlayOwnersById,
  } = useSharedAvailabilityOverlays({
    pollId: editId || null,
    ownerIds: activeOverlayOwnerIds,
  });
  // Build picker candidate descriptors. Profile data is sourced from
  // (in priority): the already-fetched projection, the saved participant
  // profile lookup (edit flow), the questing-group profile lookup, and the
  // email→profile lookup (covers email invitees in the create flow).
  const inviteEmailProfilesByUid = useMemo(() => {
    const map = {};
    const emails = [...invites, ...pendingInvites];
    if (!emails.length) return map;
    for (const profile of enrichUsers(emails)) {
      if (profile?.uid) map[profile.uid] = profile;
    }
    return map;
  }, [invites, pendingInvites, enrichUsers]);
  const overlayCandidates = useMemo(() => {
    return overlayCandidateOwnerIds.map((ownerId) => {
      const fromProjection = overlayOwnersById[ownerId];
      const fromParticipants = participantProfilesById?.[ownerId];
      const fromGroup = groupMemberProfiles?.[ownerId];
      const fromInviteEmails = inviteEmailProfilesByUid[ownerId];
      const fallback = fromParticipants || fromGroup || fromInviteEmails;
      return shapeOverlayCandidate({ ownerId, fromProjection, fallback });
    });
  }, [
    overlayCandidateOwnerIds,
    overlayOwnersById,
    participantProfilesById,
    groupMemberProfiles,
    inviteEmailProfilesByUid,
  ]);
  const activeOverlayOwners = useMemo(() => {
    return activeOverlayOwnerIds
      .map((ownerId) => {
        const candidate = overlayCandidates.find(
          (c) => c.ownerId === ownerId,
        );
        return candidate || { ownerId, displayName: null, avatarUrl: null };
      });
  }, [activeOverlayOwnerIds, overlayCandidates]);
  // Phase 4d — overlay color resolution. Sort the active list deterministically
  // so collision-avoidance produces the same colors across reloads of the same
  // poll. Per-friend overrides come from localStorage (no server field). The
  // override map is mirrored into React state so swatch picks re-render.
  const [colorOverridesByOwnerId, setColorOverridesByOwnerId] = useState({});
  const sortedActiveOverlayOwnerIds = useMemo(() => {
    return [...activeOverlayOwners]
      .sort((a, b) => {
        const an = (a.displayName || a.email || a.ownerId || "").toLowerCase();
        const bn = (b.displayName || b.email || b.ownerId || "").toLowerCase();
        if (an !== bn) return an < bn ? -1 : 1;
        return (a.ownerId || "").localeCompare(b.ownerId || "");
      })
      .map((o) => o.ownerId);
  }, [activeOverlayOwners]);
  // Stable string key so the override-load effect only re-fires when the
  // active overlay set actually changes — not on every parent re-render
  // (which would otherwise create a fresh array reference and trigger an
  // infinite setState→re-render loop). Same trick useUserProfilesByIds uses.
  const sortedActiveOverlayOwnerIdsKey = sortedActiveOverlayOwnerIds.join("|");
  useEffect(() => {
    if (!user?.uid) return;
    const ownerIds = sortedActiveOverlayOwnerIdsKey
      ? sortedActiveOverlayOwnerIdsKey.split("|")
      : [];
    const loaded = loadColorOverrides(user.uid, ownerIds);
    // Bail when the loaded map is shallow-equal to the existing one so we
    // don't churn state when the effect re-fires on no-op input changes.
    setColorOverridesByOwnerId((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(loaded);
      if (
        prevKeys.length === nextKeys.length &&
        prevKeys.every((k) => prev[k] === loaded[k])
      ) {
        return prev;
      }
      return loaded;
    });
    // sortedActiveOverlayOwnerIdsKey is the canonical content identity for
    // sortedActiveOverlayOwnerIds.
  }, [user?.uid, sortedActiveOverlayOwnerIdsKey]);
  const colorByOwnerId = useMemo(() => {
    const out = {};
    for (const ownerId of sortedActiveOverlayOwnerIds) {
      out[ownerId] = resolveOwnerColor(
        ownerId,
        sortedActiveOverlayOwnerIds,
        colorOverridesByOwnerId,
      );
    }
    return out;
  }, [sortedActiveOverlayOwnerIds, colorOverridesByOwnerId]);
  // Phase 4d — "Show my availability" overlay. Adds the viewer's own
  // events + marks to the calendar with NO glow (the lack of a colorful
  // border is the visual signal that they're yours) and full event
  // titles (no redaction — you can see your own data unredacted).
  // Persisted in localStorage, scoped to the viewer; consistent across
  // create + edit polls so a user who toggled it on in poll A sees it
  // on in poll B too.
  const [showMyAvailability, setShowMyAvailabilityState] = useState(false);
  useEffect(() => {
    if (!user?.uid) return;
    try {
      const v = window.localStorage?.getItem(
        `qs:overlay-show-self:${user.uid}`,
      );
      setShowMyAvailabilityState(v === "1");
    } catch {
      /* no-op */
    }
  }, [user?.uid]);
  const handleShowMyAvailabilityChange = (next) => {
    setShowMyAvailabilityState(!!next);
    if (!user?.uid) return;
    try {
      if (next) {
        window.localStorage?.setItem(
          `qs:overlay-show-self:${user.uid}`,
          "1",
        );
      } else {
        window.localStorage?.removeItem(`qs:overlay-show-self:${user.uid}`);
      }
    } catch {
      /* no-op */
    }
  };
  // Phase 4d — public-identifier resolution for friend-overlay tooltips.
  // Fetches usersPublic for each currently-active overlay owner so we can
  // build "(alice@example.com)" / "(@alice-qs)" / "(alice#1234)" suffixes
  // that respect the friend's own identifier-type setting.
  const { profiles: overlayActiveOwnerProfiles } = useUserProfilesByIds(
    activeOverlayOwnerIds,
  );
  const tooltipIdentifierByOwnerId = useMemo(() => {
    const out = {};
    for (const ownerId of activeOverlayOwnerIds) {
      const profile = overlayActiveOwnerProfiles?.[ownerId];
      if (!profile) continue;
      const id = getUserIdentity(profile).publicIdentifier;
      if (id) out[ownerId] = id;
    }
    return out;
  }, [activeOverlayOwnerIds, overlayActiveOwnerProfiles]);
  const handleOverlayColorChange = (ownerId, hex) => {
    if (!user?.uid || !ownerId) return;
    if (hex == null) {
      clearColorOverride(user.uid, ownerId);
      setColorOverridesByOwnerId((prev) => {
        if (!(ownerId in prev)) return prev;
        const next = { ...prev };
        delete next[ownerId];
        return next;
      });
    } else {
      setColorOverride(user.uid, ownerId, hex);
      setColorOverridesByOwnerId((prev) => ({ ...prev, [ownerId]: hex }));
    }
  };
  // Visible window — used to expand recurring overlay marks. Defaults to
  // a generous 90-day band centered on `calendarDate` so recurrences in the
  // current month/week view are covered without per-view recomputation.
  const overlayWindow = useMemo(() => {
    const center = calendarDate instanceof Date ? calendarDate : new Date();
    const winStart = new Date(center);
    winStart.setDate(winStart.getDate() - 45);
    const winEnd = new Date(center);
    winEnd.setDate(winEnd.getDate() + 45);
    return { winStart, winEnd };
  }, [calendarDate]);
  const overlayMarkEvents = useMemo(() => {
    if (!overlayMarks.length) return [];
    return expandMarksToCalendarEvents(
      overlayMarks,
      overlayWindow.winStart,
      overlayWindow.winEnd,
    );
  }, [overlayMarks, overlayWindow.winStart, overlayWindow.winEnd]);
  const overlayBusyEvents = useMemo(() => {
    return overlayBusyWindows
      .map((bw) =>
        buildBusyOverlayEvent({
          id: `overlay-busy:${bw.ownerId}:${bw.startUtc}`,
          title: "Busy",
          startUtc: bw.startUtc,
          endUtc: bw.endUtc,
          allDay: bw.allDay === true,
          resource: {
            kind: "overlay-busy",
            ownerId: bw.ownerId,
          },
        })
      )
      .filter(Boolean);
  }, [overlayBusyWindows]);

  // Self-availability data sources (fetched only when toggle is on).
  // Reads the same Firestore paths the voting calendar uses, so the
  // user sees the same conflicts + free-time blocks they see there.
  const selfAvailabilityEvents = useAvailabilityEvents(
    showMyAvailability ? user?.uid : null,
  );
  const selfAvailabilityMarks = useAvailabilityMarks(
    showMyAvailability ? user?.uid : null,
  );
  const selfBusyEvents = useMemo(() => {
    if (!showMyAvailability) return [];
    const data = selfAvailabilityEvents?.data || [];
    return data
      .map((evt) =>
        buildBusyOverlayEvent({
          id: `self-busy:${evt.id || evt.eventId || evt.startUtc || ""}`,
          // Title is resolved client-side via the enrichment hook (see
          // docs/calendar-cache-privacy-plan.md). Until resolved, an empty
          // title renders as time-only on the tile.
          title: "",
          startUtc: evt.startUtc,
          endUtc: evt.endUtc,
          allDay: evt.allDay === true,
          resource: {
            kind: "overlay-busy",
            provider: evt.provider || "google",
            calendarId: evt.calendarId || null,
            eventId: evt.eventId || null,
            ownerId: user?.uid || null,
            isSelf: true,
          },
        })
      )
      .filter(Boolean);
  }, [showMyAvailability, selfAvailabilityEvents?.data, user?.uid]);
  const selfMarkEvents = useMemo(() => {
    if (!showMyAvailability) return [];
    const marks = selfAvailabilityMarks?.data || [];
    if (!marks.length) return [];
    const expanded = expandMarksToCalendarEvents(
      marks,
      overlayWindow.winStart,
      overlayWindow.winEnd,
    );
    // Tag each expanded mark with isSelf so the eventPropGetter can
    // skip the owner-tint glow + the calendar treats it as own data.
    return expanded.map((e) => ({
      ...e,
      resource: {
        ...(e.resource || {}),
        ownerId: user?.uid || null,
        isSelf: true,
      },
    }));
  }, [
    showMyAvailability,
    selfAvailabilityMarks?.data,
    overlayWindow.winStart,
    overlayWindow.winEnd,
    user?.uid,
  ]);

  const calendarEventsStructural = useMemo(
    () => [
      ...slotEvents,
      ...overlayMarkEvents,
      ...overlayBusyEvents,
      ...selfMarkEvents,
      ...selfBusyEvents,
    ],
    [slotEvents, overlayMarkEvents, overlayBusyEvents, selfMarkEvents, selfBusyEvents],
  );
  // Resolve external-event titles client-side (privacy: titles are not
  // persisted at rest — see docs/calendar-cache-privacy-plan.md). Marks /
  // sessions / slots that lack a structural identifier triple pass through
  // untouched.
  const calendarEvents = useEnrichedCalendarEvents(calendarEventsStructural);

  // Phase 4d — per-slot overlay-vs-slot classification (warning / star /
  // checkbox indicators on each slot card). Walks the active overlay
  // owners (friends + self when toggle is on), buckets each by whether
  // their busy events / preferred marks / feasible marks overlap the
  // slot's UTC range. Self busy events use the original calendar summary
  // for the conflict tooltip; friend busy events use the projection's
  // generic "Busy" label (titles are intentionally redacted in shared
  // projections, so we don't have anything more specific to show).
  const indicatorOwners = useMemo(() => {
    const list = activeOverlayOwners.map((o) => ({ ...o, isSelf: false }));
    if (showMyAvailability && user?.uid) {
      list.push({
        ownerId: user.uid,
        displayName: user.displayName || null,
        avatarUrl: user.photoURL || null,
        email: user.email || null,
        isSelf: true,
      });
    }
    return list;
  }, [activeOverlayOwners, showMyAvailability, user]);
  const indicatorBusyByOwnerId = useMemo(() => {
    const out = {};
    for (const ev of overlayBusyEvents) {
      const oid = ev.resource?.ownerId;
      if (!oid) continue;
      (out[oid] ||= []).push({ start: ev.start, end: ev.end, title: ev.title });
    }
    for (const ev of selfBusyEvents) {
      const oid = ev.resource?.ownerId;
      if (!oid) continue;
      (out[oid] ||= []).push({ start: ev.start, end: ev.end, title: ev.title });
    }
    return out;
  }, [overlayBusyEvents, selfBusyEvents]);
  const indicatorMarksByOwnerId = useMemo(() => {
    const out = {};
    const push = (e, ownerId) => {
      if (!ownerId) return;
      const level = e.resource?.level;
      if (level !== "PREFERRED" && level !== "FEASIBLE") return;
      (out[ownerId] ||= []).push({ start: e.start, end: e.end, level });
    };
    for (const ev of overlayMarkEvents) push(ev, ev.resource?.ownerId);
    for (const ev of selfMarkEvents) push(ev, ev.resource?.ownerId);
    return out;
  }, [overlayMarkEvents, selfMarkEvents]);
  const slotIndicatorsBySlotId = useMemo(() => {
    if (!indicatorOwners.length) return {};
    return classifySlotsAgainstOverlays({
      slots: slots.map((s) => ({ id: s.id, start: s.start, end: s.end })),
      owners: indicatorOwners,
      busyEventsByOwnerId: indicatorBusyByOwnerId,
      marksByOwnerId: indicatorMarksByOwnerId,
      colorByOwnerId,
    });
  }, [
    slots,
    indicatorOwners,
    indicatorBusyByOwnerId,
    indicatorMarksByOwnerId,
    colorByOwnerId,
  ]);

  const handleToggleOverlayOwner = (ownerId) => {
    setActiveOverlayOwnerIds((prev) =>
      prev.includes(ownerId)
        ? prev.filter((id) => id !== ownerId)
        : [...prev, ownerId],
    );
  };
  const handleRemoveOverlayOwner = (ownerId) => {
    // Self chip → toggle off "Show my availability" instead of dropping
    // from the friend overlay set (self isn't actually in that list).
    if (ownerId && user?.uid && ownerId === user.uid) {
      handleShowMyAvailabilityChange(false);
      return;
    }
    setActiveOverlayOwnerIds((prev) => prev.filter((id) => id !== ownerId));
  };
  // Strip = friends + self (when self-toggle is on). Self chip is
  // synthesized from the auth context; the strip's `isSelf` flag tells
  // it to skip the color glow + label as "You".
  const stripOwners = useMemo(() => {
    const list = [...activeOverlayOwners];
    if (showMyAvailability && user?.uid) {
      list.push({
        ownerId: user.uid,
        displayName: user.displayName || null,
        email: user.email || null,
        avatarUrl: user.photoURL || null,
        isSelf: true,
      });
    }
    return list;
  }, [activeOverlayOwners, showMyAvailability, user]);

  // Phase 4b U5 — auto-drop any active overlay whose owner is no longer a
  // poll invitee (e.g. the creator removed them mid-edit). The projection
  // Cloud Function would fail anyway; dropping client-side keeps the UI
  // honest and emits a non-blocking toast so the user knows it happened.
  useEffect(() => {
    if (!activeOverlayOwnerIds.length) return;
    const candidateSet = new Set(overlayCandidateOwnerIds);
    const dropped = activeOverlayOwnerIds.filter(
      (id) => !candidateSet.has(id),
    );
    if (!dropped.length) return;
    setActiveOverlayOwnerIds((prev) =>
      prev.filter((id) => candidateSet.has(id)),
    );
    for (const ownerId of dropped) {
      const name =
        overlayOwnersById[ownerId]?.displayName ||
        participantProfilesById?.[ownerId]?.displayName ||
        "A friend";
      toast(`${name}'s availability overlay was removed`, {
        description: "They are no longer invited to this poll.",
      });
    }
  }, [
    activeOverlayOwnerIds,
    overlayCandidateOwnerIds,
    overlayOwnersById,
    participantProfilesById,
  ]);
  const {
    scrollToTime,
    selectedEventId,
    setSelectedEventId,
    hasEvents,
    hasEventsInView,
    jumpNext,
    jumpPrev,
    jumpNextWindow,
    jumpPrevWindow,
  } = useCalendarNavigation({
    events: calendarEvents,
    view: calendarView,
    date: calendarDate,
    height: 630,
    onNavigate: setCalendarDate,
  });

  const { handleAvatarClick, handleUserClick, modalProps } = useProfileInteraction();

  const calendarKey =
    calendarView === "month"
      ? `month-${calendarDate.toDateString()}`
      : `${calendarView}-${calendarDate.toDateString()}-${scrollToTime?.getTime?.() || 0}`;
  const invalidSlotIds = useMemo(() => {
    if (!isEditing) return new Set();
    const now = Date.now();
    return new Set(
      slots.filter((slot) => slot.start && new Date(slot.start).getTime() < now).map((slot) => slot.id)
    );
  }, [isEditing, slots]);
  const hasInvalidSlots = isEditing && invalidSlotIds.size > 0;

  useEffect(() => {
    if (timezoneInitialized) return;
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezoneMode === "manual" && timezone) {
      setSelectedTimezone(timezone);
    } else {
      setSelectedTimezone(detected);
    }
    setTimezoneInitialized(true);
  }, [timezoneInitialized, timezoneMode, timezone]);

  useEffect(() => {
    if (autoNag.timeZone === effectiveTimezone) return;
    setAutoNag((current) => ({ ...current, timeZone: effectiveTimezone }));
  }, [autoNag.timeZone, effectiveTimezone]);

  useEffect(() => {
    if (isEditing || selectedGroupId === reminderSettingsGroupId) return;
    if (selectedGroupId && !selectedGroup) return;
    setReminderSettings(inheritedReminderSettings);
    setReminderSettingsGroupId(selectedGroupId || null);
  }, [
    inheritedReminderSettings,
    isEditing,
    reminderSettingsGroupId,
    selectedGroup,
    selectedGroupId,
  ]);

  // Import token consumption — prefill from AI-generated JSON.
  useEffect(() => {
    if (!importToken || loadedFromImport || isEditing) return;
    const data = consumePollImportToken(importToken);
    if (!data || !data.payload) return;

    const p = data.payload;
    if (p.title != null) setTitle(p.title);
    if (p.description != null) setDescription(p.description);
    if (p.voteVisibility != null) setVoteVisibility(p.voteVisibility);
    if (p.voteAnonymization != null) setVoteAnonymization(p.voteAnonymization);
    if (p.hideVoterIdentities != null) setHideVoterIdentities(p.hideVoterIdentities);
    if (p.allowLinkSharing != null) setAllowLinkSharing(p.allowLinkSharing);
    if (p.calendarSyncEnabled != null) setCalendarSyncEnabled(p.calendarSyncEnabled);
    if (p.timezone) {
      setSelectedTimezone(p.timezone);
      setTimezoneInitialized(true);
    }
    if (p.questingGroup?.id) {
      setSelectedGroupId(p.questingGroup.id);
    }
    if (Array.isArray(p.slots) && p.slots.length > 0) {
      const importedSlots = p.slots.map((s) => ({
        id: crypto.randomUUID(),
        start: s.start instanceof Date ? s.start : new Date(s.start),
        end: s.end instanceof Date ? s.end : new Date(s.end),
      }));
      setSlots(importedSlots);
      // Navigate calendar to the first slot's month
      setCalendarDate(importedSlots[0].start);
    }
    if (Array.isArray(p.invitees) && p.invitees.length > 0) {
      const knownInvites = [];
      const unknownInvites = [];
      for (const inv of p.invitees) {
        if (friendSet.has(inv)) {
          knownInvites.push(inv);
        } else {
          unknownInvites.push(inv);
        }
      }
      if (knownInvites.length > 0) setInvites(knownInvites);
      if (unknownInvites.length > 0) setPendingInvites(unknownInvites);
    }
    if (Array.isArray(p.embeddedPolls) && p.embeddedPolls.length > 0) {
      const drafts = p.embeddedPolls.map((ep, index) => ({
        id: crypto.randomUUID(),
        title: ep.title || "",
        description: ep.description || "",
        required: ep.required === true,
        settings: ep.settings || {},
        options: (ep.options || []).map((opt, optIdx) => ({
          id: `option-${crypto.randomUUID()}`,
          label: opt.label || "",
          note: opt.note || "",
          order: optIdx,
        })),
        voteVisibility: ep.voteVisibility,
        voteAnonymization: ep.voteAnonymization,
        hideVoterIdentities: ep.hideVoterIdentities,
        order: index,
        creatorId: user?.uid || null,
      }));
      setDraftEmbeddedPolls(drafts);
    }
    setLoadedFromImport(true);
  }, [importToken, loadedFromImport, isEditing, friendSet, user?.uid]);

  useEffect(() => {
    if (!isEditing || loadedFromPoll) return;
    if (!scheduler.data || slotsSnapshot.loading) return;
    if (scheduler.data.creatorId && scheduler.data.creatorId !== user?.uid) return;
    if (explicitParticipantIds.length > 0 && explicitParticipantEmails.length === 0) return;
    const persistedGroup = scheduler.data.questingGroupId
      ? groups.find((group) => group.id === scheduler.data.questingGroupId) || null
      : null;
    if (
      scheduler.data.reminders === undefined &&
      scheduler.data.questingGroupId &&
      !persistedGroup
    ) return;
    setTitle(scheduler.data.title || "");
    setDescription(scheduler.data.description || "");
    setAllowLinkSharing(Boolean(scheduler.data.allowLinkSharing));
    setCalendarSyncEnabled(scheduler.data.calendarSyncEnabled !== false);
    const loadedDeadlineLocal = toSessionDeadlineLocalValue(
      resolveSchedulerVotingDeadline(scheduler.data),
      scheduler.data.timezone || browserTimezone
    );
    setVotingDeadlineLocal(loadedDeadlineLocal);
    setInitialVotingDeadlineLocal(loadedDeadlineLocal);
    setReminderSettings(createReminderSettingsSnapshot(
      scheduler.data.reminders ?? persistedGroup?.reminders,
      { discordLinked: Boolean(persistedGroup?.discord?.channelId) }
    ));
    setReminderSettingsGroupId(scheduler.data.questingGroupId || null);
    setAutoNag(createAutoNagSettingsSnapshot(scheduler.data.autoNag, {
      timeZone: scheduler.data.timezone || browserTimezone,
    }));
    const schedulerVoteVisibility = resolveVoteVisibility(scheduler.data.voteVisibility);
    setVoteVisibility(schedulerVoteVisibility);
    setVoteAnonymization(resolveVoteAnonymization(scheduler.data.voteAnonymization));
    setHideVoterIdentities(
      resolveHideVoterIdentitiesForVisibility(
        scheduler.data.hideVoterIdentities === true,
        schedulerVoteVisibility
      )
    );
    const creatorEmail = scheduler.data.creatorEmail || user?.email;
    setInvites(explicitParticipantEmails.filter((email) => email && email !== creatorEmail));
    const pendingList = (scheduler.data.pendingInvites || [])
      .filter((email) => email && email !== creatorEmail)
      .map((email) => normalizeEmail(email));
    setPendingInvites(pendingList);
    if (scheduler.data.questingGroupId && !selectedGroupId) {
      setSelectedGroupId(scheduler.data.questingGroupId);
    }
    setSlots(
      slotsSnapshot.data.map((slot) => ({
        id: slot.id,
        start: slot.start ? new Date(slot.start) : new Date(),
        end: slot.end ? new Date(slot.end) : new Date(),
        persisted: true,
      }))
    );
    setInitialSlotIds(new Set(slotsSnapshot.data.map((slot) => slot.id)));
    const slotTimes = {};
    slotsSnapshot.data.forEach((slot) => {
      const start = slot.start ? new Date(slot.start).getTime() : null;
      const end = slot.end ? new Date(slot.end).getTime() : null;
      slotTimes[slot.id] = { start, end };
    });
    setInitialSlotTimes(slotTimes);
    if (scheduler.data.timezone) {
      setSelectedTimezone(scheduler.data.timezone);
      setTimezoneInitialized(true);
    }
    // Audit A-2: open the calendar on the earliest slot's month when none of an
    // edited poll's slots fall in the displayed (today's) month — otherwise the
    // month-scoped mobile agenda + the RBC grid open empty for a future poll.
    // Decide on the poll's own timezone (matching where the agenda will bucket
    // the slots once `selectedTimezone` re-renders); when the poll stores none,
    // `toDisplayDate` falls back to the browser zone, which is exactly the
    // effective timezone at edit-load. Reading the poll field (not the reactive
    // `effectiveTimezone`) keeps this one-shot effect free of a tz dependency.
    const editTz = scheduler.data.timezone || undefined;
    const editDisplayStarts = slotsSnapshot.data
      .map((slot) =>
        slot.start ? toDisplayDate(new Date(slot.start), editTz) : null
      )
      .filter(Boolean);
    const editNavTarget = resolveInitialCalendarMonth(
      editDisplayStarts,
      new Date()
    );
    if (editNavTarget) setCalendarDate(editNavTarget);
    setLoadedFromPoll(true);
  }, [
    isEditing,
    loadedFromPoll,
    scheduler.data,
    slotsSnapshot.loading,
    slotsSnapshot.data,
    user?.uid,
    user?.email,
    browserTimezone,
    explicitParticipantIds.length,
    explicitParticipantEmails,
    selectedGroupId,
    groups,
  ]);

  useEffect(() => {
    if (!selectedGroup) return;
    setInvites((prev) => prev.filter((email) => !groupMemberSet.has(normalizeEmail(email))));
    setPendingInvites((prev) => prev.filter((email) => !groupMemberSet.has(normalizeEmail(email))));
  }, [selectedGroup, groupMemberSet]);

  useEffect(() => {
    if (!isEditing) return;
    const groupId = scheduler.data?.questingGroupId;
    if (!groupId) {
      setSelectedGroupId(null);
      return;
    }
    if (selectedGroupId && selectedGroupId !== groupId) {
      return;
    }
    if (!groups.length) return;
    const exists = groups.find((group) => group.id === groupId);
    if (exists) {
      setSelectedGroupId(groupId);
    } else {
      setSelectedGroupId(null);
    }
  }, [isEditing, scheduler.data?.questingGroupId, groups, selectedGroupId]);

  const slotVoters = useMemo(() => {
    if (!isEditing) return {};
    const map = {};
    votesSnapshot.data.forEach((voteDoc) => {
      if (!voteDoc?.userEmail) return;
      const userInfo = { email: voteDoc.userEmail, avatar: voteDoc.userAvatar };
      Object.entries(voteDoc.votes || {}).forEach(([slotId, value]) => {
        if (!map[slotId]) {
          map[slotId] = { preferred: [], feasible: [] };
        }
        if (value === "PREFERRED") {
          map[slotId].preferred = uniqueUsers([...map[slotId].preferred, userInfo]);
          map[slotId].feasible = uniqueUsers([...map[slotId].feasible, userInfo]);
        } else if (value === "FEASIBLE") {
          map[slotId].feasible = uniqueUsers([...map[slotId].feasible, userInfo]);
        }
      });
    });
    return map;
  }, [isEditing, votesSnapshot.data]);

  const tallies = useMemo(() => {
    if (!isEditing) return {};
    const map = {};
    votesSnapshot.data.forEach((voteDoc) => {
      Object.entries(voteDoc.votes || {}).forEach(([slotId, value]) => {
        if (!map[slotId]) map[slotId] = { feasible: 0, preferred: 0 };
        if (value === "PREFERRED") {
          map[slotId].preferred += 1;
          map[slotId].feasible += 1;
        } else if (value === "FEASIBLE") {
          map[slotId].feasible += 1;
        }
      });
    });
    return map;
  }, [isEditing, votesSnapshot.data]);

  const colorMap = useMemo(() => {
    if (!isEditing) return {};
    const voterEmails = votesSnapshot.data.map((voteDoc) => voteDoc.userEmail).filter(Boolean);
    const set = new Set([...(explicitParticipantEmails || []), ...voterEmails]);
    return buildColorMap(Array.from(set).sort((a, b) => a.localeCompare(b)));
  }, [isEditing, explicitParticipantEmails, votesSnapshot.data]);

  // R14: the creator-edit view must show ALIASES (not real names) when the poll
  // uses `all_participants` anonymization. The creator is never hidden — only
  // aliased — so there is no placeholder path here; we just anonymize the slot
  // voter stacks (self-exempting the creator). `voteAnonymization`/`hideVoterIdentities`
  // come from scheduler.data when editing; the creator always has detailsRevealed.
  const creatorVoteIdentityDisplayMode = useMemo(() => {
    if (!isEditing) return "named";
    return getVoteIdentityDisplayMode({
      isCreator: true,
      hideVoterIdentities: scheduler.data?.hideVoterIdentities,
      voteAnonymization: scheduler.data?.voteAnonymization,
      detailsRevealed: true,
    });
  }, [isEditing, scheduler.data?.hideVoterIdentities, scheduler.data?.voteAnonymization]);
  const slotVotersForDisplay = useMemo(
    () =>
      anonymizeSlotVotersForCreator(slotVoters, {
        mode: creatorVoteIdentityDisplayMode,
        currentUser: user,
        scopeKey: `creator-edit:${editId}`,
      }),
    [slotVoters, creatorVoteIdentityDisplayMode, user, editId]
  );
  // No real-email color map in anonymous mode (avatars are anonymized stubs).
  const creatorColorMap = creatorVoteIdentityDisplayMode === "anonymous" ? {} : colorMap;
  const displayedEmbeddedPolls = isEditing ? embeddedPolls : draftEmbeddedPolls;
  const embeddedPollParticipantCount = useMemo(() => {
    if (isEditing) {
      const participantIds = new Set([
        ...(scheduler.data?.participantIds || []),
        ...(groupMemberIds || []),
      ]);
      return participantIds.size;
    }

    const participantEmails = new Set([
      normalizeEmail(user?.email),
      ...inviteEmails.map((email) => normalizeEmail(email)),
      ...groupMemberEmails.map((email) => normalizeEmail(email)),
    ]);
    participantEmails.delete("");
    return participantEmails.size;
  }, [
    groupMemberEmails,
    groupMemberIds,
    inviteEmails,
    isEditing,
    scheduler.data?.participantIds,
    user?.email,
  ]);
  const embeddedPollSensors = useSensors(useSensor(PointerSensor));

  // Shared slot-edit-modal opener keyed by slot id. Both the calendar grid's
  // quick-add event tap (via slotActionsRef) and the mobile agenda's row tap
  // route through this single path so lookup + draft population can never
  // drift apart.
  const openEditSlotModalById = useCallback(
    (slotId) => {
      const slot = slots.find((s) => s.id === slotId);
      if (!slot) return;
      const slotStart = slot.start instanceof Date ? slot.start : new Date(slot.start);
      const slotEnd = slot.end instanceof Date ? slot.end : new Date(slot.end);
      const displayStart = toDisplayDate(slotStart, effectiveTimezone);
      setDraftDate(displayStart);
      setDraftTime(format(displayStart, "HH:mm"));
      setDraftDuration(Math.round((slotEnd - slotStart) / 60000));
      setEditingSlotId(slot.id);
      setModalOpen(true);
    },
    [slots, effectiveTimezone],
  );

  // Stable ref so the calendar event component can call the latest handlers
  // without the component identity changing on every render.
  const slotActionsRef = useRef(null);
  slotActionsRef.current = {
    isQuickAddMode: quickAddMode,
    removeSlot: (slotId) => setSlots((prev) => prev.filter((slot) => slot.id !== slotId)),
    openEditSlotModal: (event) => openEditSlotModalById(event.id),
  };

  // Custom event component — stable identity so react-big-calendar doesn't
  // remount all events on every render. Reads callbacks from slotActionsRef.
  // Phase 4b: overlay events (kind="mark" / "overlay-busy") are read-only —
  // no Remove-slot affordance, no quick-add edit shortcut. Slot events keep
  // their existing UI.
  const eventComponent = useMemo(() => {
    function CalendarEventComponent({ event, title }) {
      const actions = slotActionsRef.current;
      const kind = event?.resource?.kind;
      const isOverlay = kind === "mark" || kind === "overlay-busy";
      if (isOverlay) {
        return (
          <div
            className="flex h-full w-full items-start overflow-hidden"
            data-overlay-kind={kind}
          >
            <span className="flex-1 truncate text-xs leading-tight">{title}</span>
          </div>
        );
      }
      return (
        <div
          className="group relative flex h-full w-full items-start overflow-hidden"
          onClick={() => {
            if (actions.isQuickAddMode) actions.openEditSlotModal(event);
          }}
        >
          <span className="flex-1 truncate text-xs leading-tight">{title}</span>
          <button
            type="button"
            aria-label="Remove slot"
            onClick={(e) => {
              e.stopPropagation();
              actions.removeSlot(event.id);
            }}
            className="ml-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white/80 text-slate-700 opacity-0 transition-opacity group-hover:opacity-100 [@media(hover:none)]:h-6 [@media(hover:none)]:w-6 [@media(hover:none)]:opacity-100 dark:bg-slate-800/80 dark:text-slate-200"
          >
            <X className="h-2.5 w-2.5 [@media(hover:none)]:h-3.5 [@media(hover:none)]:w-3.5" />
          </button>
        </div>
      );
    }
    return CalendarEventComponent;
    // Intentionally empty deps: component is stable and reads callbacks from slotActionsRef (a ref).
  }, []);

  const removeSlot = (slotId) => {
    setSlots((prev) => prev.filter((slot) => slot.id !== slotId));
  };

  const openModalForDate = (date, overrides = {}) => {
    const safeDate = date instanceof Date ? date : new Date(date);
    setDraftDate(safeDate);
    const weekday = getDay(safeDate);
    const sessionDefaults = getSessionDefaults(weekday);
    // `overrides` lets Quick Add pre-fill a smart, collision-free start time
    // (just after the day's last slot) when adding a second/third slot.
    setDraftTime(overrides.time ?? sessionDefaults.time);
    setDraftDuration(overrides.durationMinutes ?? sessionDefaults.durationMinutes);
    setModalOpen(true);
  };

  const saveDraftSlot = () => {
    if (!draftDate || !draftTime) {
      console.error("Missing draft date/time", { draftDate, draftTime });
      toast.error("Select a date and time before adding a slot");
      return;
    }
    const dateStr = format(draftDate, "yyyy-MM-dd");
    const startUtc = fromZonedTime(`${dateStr}T${draftTime}:00`, effectiveTimezone);

    // Validate that the slot isn't in the past
    if (startUtc < new Date()) {
      toast.error("Cannot add a slot in the past. Please select a future time.");
      return;
    }

    const endUtc = new Date(
      startUtc.getTime() + Number(draftDuration || 0) * 60 * 1000
    );

    if (editingSlotId) {
      setSlots((prev) =>
        prev.map((slot) =>
          slot.id === editingSlotId ? { ...slot, start: startUtc, end: endUtc } : slot
        )
      );
      setEditingSlotId(null);
    } else {
      setSlots((prev) => [
        ...prev,
        { id: crypto.randomUUID(), start: startUtc, end: endUtc },
      ]);
    }
    setModalOpen(false);
  };

  const addSlotFromSelection = (slotInfo) => {
    if (!slotInfo?.start) return;
    const startDate = slotInfo.start instanceof Date ? slotInfo.start : new Date(slotInfo.start);

    // Block adding slots in the past (5s tolerance for click-to-process delay)
    if (startDate.getTime() < Date.now() - 5000) {
      return; // Silently ignore - visual cues already indicate non-interactivity
    }

    const start = fromZonedTime(
      format(startDate, "yyyy-MM-dd'T'HH:mm:ss"),
      effectiveTimezone
    );
    const selectedMinutes =
      slotInfo.end && slotInfo.end > slotInfo.start
        ? Math.round((slotInfo.end - slotInfo.start) / 60000)
        : 0;
    const durationMinutes =
      selectedMinutes > 30 ? selectedMinutes : defaultDuration;
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
    setSlots((prev) => [...prev, { id: crypto.randomUUID(), start, end }]);
  };


  const updateSlotTimes = (slotId, start, end) => {
    setSlots((prev) =>
      prev.map((slot) => (slot.id === slotId ? { ...slot, start, end } : slot))
    );
  };

  const openAddEmbeddedPoll = () => {
    setEditingEmbeddedPoll(null);
    setEmbeddedPollEditorOpen(true);
  };

  const openEditEmbeddedPoll = (poll) => {
    setEditingEmbeddedPoll(poll);
    setEmbeddedPollEditorOpen(true);
  };

  const handleSaveEmbeddedPoll = async (pollPayload) => {
    if (isEditing && !editId) return;
    setEmbeddedPollSaveBusy(true);
    try {
      if (editingEmbeddedPoll?.id) {
        if (isEditing) {
          const previousRequired = editingEmbeddedPoll.required === true;
          const nextRequired = pollPayload.required === true;
          await updateEmbeddedBasicPoll(editId, editingEmbeddedPoll.id, pollPayload);
          if (previousRequired !== nextRequired) {
            await notifyEmbeddedBasicPollRequiredChanged(editId, editingEmbeddedPoll.id);
          }
        } else {
          setDraftEmbeddedPolls((previous) =>
            upsertEmbeddedPollDraft(previous, pollPayload, {
              pollId: editingEmbeddedPoll.id,
              creatorId: user?.uid || null,
            })
          );
        }
        toast.success("Add-on poll updated");
        setEditingEmbeddedPoll(null);
        return;
      }
      if (isEditing) {
        const currentMaxOrder = embeddedPolls.reduce((maxOrder, poll) => {
          const orderValue = Number.isFinite(poll?.order) ? poll.order : 0;
          return Math.max(maxOrder, orderValue);
        }, -1);
        await createEmbeddedBasicPoll(
          editId,
          {
            ...pollPayload,
            order: currentMaxOrder + 1,
            creatorId: user?.uid || null,
          },
          { useServer: true }
        );
      } else {
        setDraftEmbeddedPolls((previous) =>
          upsertEmbeddedPollDraft(previous, pollPayload, {
            pollId: null,
            creatorId: user?.uid || null,
          })
        );
      }
      toast.success("Add-on poll added");
      setEditingEmbeddedPoll(null);
    } finally {
      setEmbeddedPollSaveBusy(false);
    }
  };

  const handleAddonImport = async (normalizedPolls) => {
    if (!normalizedPolls || normalizedPolls.length === 0) return;

    if (normalizedPolls.length === 1) {
      // Single poll: open the editor modal for user to tweak before confirming.
      setEditingEmbeddedPoll(normalizedPolls[0]);
      setEmbeddedPollEditorOpen(true);
      return;
    }

    // Multiple polls.
    if (!isEditing) {
      // Draft mode: upsert each into draft state.
      setDraftEmbeddedPolls((previous) => {
        let updated = previous;
        for (const poll of normalizedPolls) {
          updated = upsertEmbeddedPollDraft(updated, poll, {
            pollId: null,
            creatorId: user?.uid || null,
          });
        }
        return updated;
      });
      toast.success(`${normalizedPolls.length} add-on polls imported`);
      return;
    }

    // Edit mode: create each in Firestore.
    try {
      const currentMaxOrder = embeddedPolls.reduce((maxOrder, poll) => {
        const orderValue = Number.isFinite(poll?.order) ? poll.order : 0;
        return Math.max(maxOrder, orderValue);
      }, -1);

      await Promise.all(
        normalizedPolls.map((poll, index) =>
          createEmbeddedBasicPoll(
            editId,
            {
              ...poll,
              order: currentMaxOrder + 1 + index,
              creatorId: user?.uid || null,
            },
            { useServer: true }
          )
        )
      );
      toast.success(`${normalizedPolls.length} add-on polls imported`);
    } catch (error) {
      console.error("Failed to import add-on polls:", error);
      toast.error(error?.message || "Failed to import add-on polls");
    }
  };

  const confirmDeleteEmbeddedPoll = (poll) => {
    setEmbeddedPollToDelete(poll);
    setDeleteEmbeddedPollOpen(true);
  };

  const handleDeleteEmbeddedPoll = async () => {
    if (!embeddedPollToDelete?.id || embeddedPollDeleteBusy) return;
    setEmbeddedPollDeleteBusy(true);
    try {
      if (isEditing) {
        await deleteEmbeddedBasicPoll(editId, embeddedPollToDelete.id, { useServer: true });
      } else {
        setDraftEmbeddedPolls((previous) =>
          removeEmbeddedPollDraft(previous, embeddedPollToDelete.id)
        );
      }
      toast.success("Add-on poll removed");
      setDeleteEmbeddedPollOpen(false);
      setEmbeddedPollToDelete(null);
    } catch (error) {
      console.error("Failed to delete add-on poll:", error);
      toast.error(error?.message || "Failed to remove add-on poll");
    } finally {
      setEmbeddedPollDeleteBusy(false);
    }
  };

  const handleEmbeddedPollDragEnd = async (event) => {
    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) return;
    const reordered = reorderEmbeddedPollDrafts(displayedEmbeddedPolls, active.id, over.id);

    if (isEditing) {
      if (!editId) return;
      setEmbeddedPolls(reordered);
      try {
        await reorderEmbeddedBasicPolls(
          editId,
          reordered.map((poll) => poll.id)
        );
      } catch (error) {
        console.error("Failed to reorder add-on polls:", error);
        toast.error(error?.message || "Failed to reorder add-on polls");
      }
      return;
    }

    setDraftEmbeddedPolls(reordered);
  };

  const sendPendingInvites = async (pendingRecipients, schedulerId, pollTitle) => {
    const pending = pendingRecipients || [];
    if (pending.length === 0) return { added: [], rejected: [] };
    const response = await sendPendingPollInvites(schedulerId, pending, pollTitle);
    const rejected = response?.rejected || [];

    if (rejected.length > 0) {
      const blocked = rejected.filter((item) => item.reason === "blocked").map((item) => item.email);
      const limited = rejected.filter((item) => item.reason === "limit").map((item) => item.email);
      if (blocked.length > 0) {
        toast.error(`Couldn't invite: ${blocked.join(", ")} (blocked).`);
      }
      if (limited.length > 0) {
        toast.error(`Invite limit reached for: ${limited.join(", ")}.`);
      }
    }

    return response;
  };

  const resolveParticipantIdsByEmail = async (emails) => {
    const normalized = normalizeEmailList(emails);
    const resolved = await findUserIdsByEmails(normalized);
    if (user?.uid && user?.email) {
      resolved[normalizeEmail(user.email)] = user.uid;
    }
    return resolved;
  };

  const getPollInputs = () => {
    const explicitParticipants = Array.from(
      new Set([user.email, ...inviteEmails].filter(Boolean).map(normalizeEmail))
    );
    const pendingList = Array.from(
      new Set(pendingInvites.filter(Boolean).map(normalizeEmail))
    ).filter(
      (email) => !explicitParticipants.includes(email) && !groupMemberSet.has(email)
    );
    const creatorEmail = normalizeEmail(user.email);
    const pollTitle = title || "Untitled poll";
    const pollDescription = (description || "").trim();
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneModeForScheduler =
      selectedTimezone === detectedTimezone ? "auto" : "manual";
    const nextVoteVisibility = resolveVoteVisibility(voteVisibility);
    const deadlineResult = validateSessionDeadlineChange({
      localValue: votingDeadlineLocal,
      timeZone: effectiveTimezone,
      initialLocalValue: initialVotingDeadlineLocal,
      isEditing,
      slots,
    });
    if (!deadlineResult.valid) {
      throw new Error(deadlineResult.error || "Voting deadline is invalid.");
    }
    const autoNagResult = validateAutoNagAuthoring({
      config: autoNag,
      votingDeadlineAt: deadlineResult.deadlineAt,
      earliestSlotAt: earliestProposedSlotAt,
    });
    if (!autoNagResult.valid) {
      throw new Error(autoNagResult.error || "Auto-nag schedule is invalid.");
    }
    return {
      explicitParticipants,
      pendingList,
      creatorEmail,
      pollTitle,
      pollDescription,
      timezoneModeForScheduler,
      voteVisibility: nextVoteVisibility,
      voteAnonymization: voteAnonymizationLocked
        ? VOTE_ANONYMIZATION.ALL_PARTICIPANTS
        : resolveVoteAnonymization(voteAnonymization),
      hideVoterIdentities: resolveHideVoterIdentitiesForVisibility(
        hideVoterIdentities === true,
        nextVoteVisibility
      ),
      votingDeadlineAt: deadlineResult.deadlineAt,
    };
  };

  const deleteCalendarEntry = async () => {
    if (!editId) return;
    const functions = getFunctions();
    const deleteEvent = httpsCallable(functions, "googleCalendarDeleteEvent");
    await deleteEvent({ schedulerId: editId });
  };

  const saveEdits = async ({ updateCalendar } = {}) => {
    if (!schedulerDocRef || !editId) return false;
    setSubmitting(true);
    let success = false;
    try {
      const {
        explicitParticipants,
        pendingList,
        creatorEmail,
        pollTitle,
        pollDescription,
        timezoneModeForScheduler,
        voteVisibility: nextVoteVisibility,
        voteAnonymization: nextVoteAnonymization,
        hideVoterIdentities: nextHideVoterIdentities,
        votingDeadlineAt,
      } = getPollInputs();
      const inviteRecipients = Array.from(
        new Set([...explicitParticipants, ...pendingList].filter(Boolean))
      )
        .map((email) => normalizeEmail(email))
        .filter(
          (email) =>
            email &&
            email !== creatorEmail &&
            !groupMemberSet.has(email)
        );
      const participantIdMap = await resolveParticipantIdsByEmail([
        creatorEmail,
        ...inviteRecipients,
      ]);
      const participantIds = Array.from(
        new Set(Object.values(participantIdMap).filter(Boolean))
      );

      if (updateCalendar && scheduler.data?.googleEventId) {
        await deleteCalendarEntry();
      }

      const previousParticipantIds = new Set(scheduler.data?.participantIds || []);
      const previousPending = new Set(
        (scheduler.data?.pendingInvites || []).map((email) => normalizeEmail(email))
      );
      const inviteRecipientSet = new Set(inviteRecipients);
      const newPendingRecipients = inviteRecipients.filter((email) => {
        if (previousPending.has(email)) return false;
        const userId = participantIdMap[email];
        if (userId && previousParticipantIds.has(userId)) return false;
        return true;
      });
      const removedPendingRecipients = Array.from(previousPending).filter(
        (email) => !inviteRecipientSet.has(email)
      );
      const combinedParticipantIds = new Set([...participantIds, ...groupMemberIds].filter(Boolean));
      const submittedVoteIds = new Set(
        votesSnapshot.data
          .filter((voteDoc) => hasSubmittedSchedulerVote(voteDoc))
          .map((voteDoc) => String(voteDoc?.id || "").trim())
          .filter(Boolean)
      );
      const votesAllSubmitted =
        combinedParticipantIds.size > 0 &&
        Array.from(combinedParticipantIds).every((participantId) =>
          submittedVoteIds.has(String(participantId))
        );
      await updateScheduler(editId, {
        title: pollTitle,
        description: pollDescription,
        participantIds,
        allowLinkSharing,
        timezone: effectiveTimezone,
        timezoneMode: timezoneModeForScheduler,
        questingGroupId: selectedGroup?.id || null,
        questingGroupName: selectedGroup?.name || null,
        voteVisibility: nextVoteVisibility,
        voteAnonymization: nextVoteAnonymization,
        hideVoterIdentities: nextHideVoterIdentities,
        votingDeadlineAt,
        reminders: reminderSettings,
        autoNag,
        calendarSyncEnabled,
        votesAllSubmitted,
        participants: deleteField(),
        updatedAt: serverTimestamp(),
      });

      const currentSlotIds = new Set(slots.map((slot) => slot.id));
      const removedIds = Array.from(initialSlotIds).filter(
        (slotId) => !currentSlotIds.has(slotId)
      );
      const addedCount = slots.filter((slot) => !initialSlotIds.has(slot.id)).length;
      const changedCount = slots.filter((slot) => {
        if (!initialSlotIds.has(slot.id)) return false;
        const initial = initialSlotTimes[slot.id];
        if (!initial) return false;
        const start = slot.start instanceof Date ? slot.start.getTime() : new Date(slot.start).getTime();
        const end = slot.end instanceof Date ? slot.end.getTime() : new Date(slot.end).getTime();
        return start !== initial.start || end !== initial.end;
      }).length;
      const removedCount = removedIds.length;
      const hasSlotChanges = addedCount + changedCount + removedCount > 0;
      const summaryParts = [];
      if (addedCount) summaryParts.push(`${addedCount} slot${addedCount === 1 ? "" : "s"} added`);
      if (removedCount) summaryParts.push(`${removedCount} slot${removedCount === 1 ? "" : "s"} removed`);
      if (changedCount) summaryParts.push(`${changedCount} slot${changedCount === 1 ? "" : "s"} updated`);
      const changeSummary = summaryParts.join(", ");

      await Promise.all(
        slots.map((slot) => {
          const data = {
            start: slot.start.toISOString(),
            end: slot.end.toISOString(),
          };
          if (!initialSlotIds.has(slot.id)) {
            data.stats = { feasible: 0, preferred: 0 };
          }
          return upsertSchedulerSlot(editId, slot.id, data);
        })
      );

      const allowedParticipantIds = new Set([
        ...participantIds,
        ...groupMemberIds,
      ]);

      if (removedIds.length > 0) {
        await Promise.all(
          removedIds.map((slotId) => deleteSchedulerSlot(editId, slotId))
        );
      }

      await Promise.all(
        votesSnapshot.data.map((voteDoc) => {
          const voterId = voteDoc.id;
          // Guest votes are intentionally NOT participants — never prune them on
          // a creator edit (that would wipe every guest's vote). They fall
          // through to the removed-slot-key cleanup below like any other vote.
          if (voterId && !allowedParticipantIds.has(voterId) && !voteDoc.isGuest) {
            return deleteSchedulerVote(editId, voteDoc.id);
          }
          const votes = voteDoc.votes || {};
          let changed = false;
          const nextVotes = { ...votes };
          removedIds.forEach((slotId) => {
            if (nextVotes[slotId]) {
              delete nextVotes[slotId];
              changed = true;
            }
          });
          if (!changed) return Promise.resolve();
          return upsertSchedulerVote(editId, voteDoc.id, {
            votes: nextVotes,
            updatedAt: serverTimestamp(),
          });
        })
      );

      if (removedPendingRecipients.length > 0) {
        await Promise.allSettled(
          removedPendingRecipients.map((email) => revokePollInvite(editId, email))
        );
      }

      if (newPendingRecipients.length > 0) {
        try {
          await sendPendingInvites(newPendingRecipients, editId, pollTitle);
        } catch (inviteErr) {
          console.error("Failed to send pending invites:", inviteErr);
          toast.error(inviteErr?.message || "Failed to send pending invites.");
        }
      }

      if (hasSlotChanges) {
        try {
          const recipientUserIds = Array.from(
            new Set([...participantIds, ...groupMemberIds].filter(Boolean))
          ).filter((participantId) => participantId !== user?.uid);
          if (recipientUserIds.length > 0) {
            await emitPollEvent({
              eventType: "SLOT_CHANGED",
              schedulerId: editId,
              pollTitle,
              actor: buildNotificationActor(user),
              payload: {
                pollTitle,
                changeSummary,
              },
              recipients: {
                userIds: recipientUserIds,
                emails: [],
              },
              dedupeKey: `poll:${editId}:slot-change`,
            });
          }
        } catch (notifyErr) {
          console.error("Failed to notify participants about slot updates:", notifyErr);
        }
      }

      navigate(`/scheduler/${editId}`);
      success = true;
    } catch (err) {
      console.error("Failed to save session poll:", err);
      toast.error(err.message || "Failed to save session poll");
    } finally {
      setSubmitting(false);
    }
    return success;
  };

  const createPoll = async () => {
    setSubmitting(true);
    try {
      const {
        explicitParticipants,
        pendingList,
        creatorEmail,
        pollTitle,
        pollDescription,
        timezoneModeForScheduler,
        voteVisibility: nextVoteVisibility,
        voteAnonymization: nextVoteAnonymization,
        hideVoterIdentities: nextHideVoterIdentities,
        votingDeadlineAt,
      } = getPollInputs();
      const inviteRecipients = Array.from(
        new Set([...explicitParticipants, ...pendingList].filter(Boolean))
      )
        .map((email) => normalizeEmail(email))
        .filter(
          (email) =>
            email &&
            email !== creatorEmail &&
            !groupMemberSet.has(email)
        );
      const participantIdMap = await resolveParticipantIdsByEmail([
        creatorEmail,
        ...inviteRecipients,
      ]);
      const participantIds = Array.from(
        new Set(Object.values(participantIdMap).filter(Boolean))
      );

      const schedulerId = crypto.randomUUID();

      await setScheduler(schedulerId, {
        title: pollTitle,
        description: pollDescription,
        creatorId: user.uid,
        creatorEmail: user.email,
        status: "OPEN",
        participantIds,
        pendingInvites: [],
        allowLinkSharing,
        timezone: effectiveTimezone,
        timezoneMode: timezoneModeForScheduler,
        winningSlotId: null,
        googleEventId: null,
        voteVisibility: nextVoteVisibility,
        voteAnonymization: nextVoteAnonymization,
        hideVoterIdentities: nextHideVoterIdentities,
        votingDeadlineAt,
        reminders: reminderSettings,
        autoNag,
        votesAllSubmitted: false,
        questingGroupId: selectedGroup?.id || null,
        questingGroupName: selectedGroup?.name || null,
        calendarSyncEnabled,
        createdAt: serverTimestamp(),
      });

      await Promise.all(
        slots.map((slot) => {
          return addSchedulerSlot(schedulerId, {
            start: slot.start.toISOString(),
            end: slot.end.toISOString(),
            stats: { feasible: 0, preferred: 0 },
          });
        })
      );

      let embeddedPollCreateError = null;
      const embeddedPollPayloads = toEmbeddedPollCreatePayloads(
        draftEmbeddedPolls,
        user?.uid || null
      );
      if (embeddedPollPayloads.length > 0) {
        try {
          await Promise.all(
            embeddedPollPayloads.map((pollPayload) =>
              createEmbeddedBasicPoll(schedulerId, pollPayload, { useServer: true })
            )
          );
        } catch (embeddedError) {
          embeddedPollCreateError = embeddedError;
          console.error("Failed to create one or more add-on polls:", embeddedError);
        }
      }

      if (inviteRecipients.length > 0) {
        try {
          await sendPendingInvites(inviteRecipients, schedulerId, pollTitle);
        } catch (inviteErr) {
          console.error("Failed to send pending invites:", inviteErr);
          toast.error(inviteErr?.message || "Failed to send pending invites.");
        }
      }

      try {
        const recipientUserIds = participantIds.filter((participantId) => participantId !== user?.uid);
        await emitPollEvent({
          eventType: "POLL_CREATED",
          schedulerId,
          pollTitle,
          actor: buildNotificationActor(user),
          payload: {
            pollTitle,
          },
          recipients: {
            userIds: recipientUserIds,
            emails: inviteRecipients,
          },
          dedupeKey: `poll:${schedulerId}:created`,
        });
      } catch (notifyErr) {
        console.error("Failed to emit poll created notification:", notifyErr);
      }

      setCreatedId(schedulerId);
      toast.success("Session poll created");
      if (embeddedPollCreateError) {
        toast.error("Session created, but one or more add-on polls failed to save.");
      }
      navigate(`/scheduler/${schedulerId}`);
    } catch (err) {
      console.error("Failed to save session poll:", err);
      toast.error(err.message || "Failed to save session poll");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();

    if (!user) {
      console.error("Create poll blocked: user not signed in");
      toast.error("You must be signed in to create a session poll");
      return;
    }

    if (!slots.length) {
      console.error("Create poll blocked: no slots");
      toast.error("Add at least one slot");
      return;
    }

    if (hasInvalidSlots) {
      console.error("Create poll blocked: past slots", { invalidSlotIds });
      toast.error("Remove past slots before saving");
      return;
    }

    if (isEditing) {
      if (scheduler.data?.googleEventId) {
        setCalendarUpdateChecked(false);
        setCalendarUpdateOpen(true);
        return;
      }
      await saveEdits();
      return;
    }

    await createPoll();
  };

  const confirmEditSave = async () => {
    const success = await saveEdits({ updateCalendar: calendarUpdateChecked });
    if (success) {
      setCalendarUpdateOpen(false);
      setCalendarUpdateChecked(false);
    }
  };

  const addInvite = async (input) => {
    const raw = String(input || "").trim();
    if (!raw) return;
    let resolved;
    try {
      resolved = await resolveIdentifier(raw);
    } catch (err) {
      setInviteError(err.message || "Enter a valid email or Discord username.");
      return;
    }
    const validation = validateInviteCandidate({
      email: resolved.email,
      selfEmail: user?.email,
      groupMemberSet,
      existingInvites: invites,
      pendingInvites,
    });
    if (!validation.ok) {
      setInviteError(validation.error);
      return;
    }
    const normalized = validation.normalized;
    if (friendSet.has(normalized)) {
      setInvites((prev) => [...prev, normalized]);
    } else {
      setPendingInvites((prev) => [...prev, normalized]);
    }
    setInviteInput("");
    setInviteError(null);
  };

  const removeInvite = (email) => {
    setInvites((prev) => prev.filter((item) => item !== email));
  };

  const removePendingInvite = (email) => {
    setPendingInvites((prev) => prev.filter((item) => item !== email));
  };

  const handleGroupChange = (groupId) => {
    if (!groupId || groupId === "none") {
      setSelectedGroupId(null);
      setReminderSettings(createReminderSettingsSnapshot(undefined));
      setReminderSettingsGroupId(null);
      return;
    }
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;
    setSelectedGroupId(groupId);
    setReminderSettings(createReminderSettingsSnapshot(group.reminders, {
      discordLinked: Boolean(group?.discord?.channelId),
    }));
    setReminderSettingsGroupId(groupId);
  };

  // Shared precedence (loading → error → not-found → …). The editor waits for
  // BOTH the poll doc and its slots sub-collection (audit A-8: gating only on
  // `scheduler.loading` flashed the agenda's "No slots proposed yet" after the
  // doc arrived but before its slots did), and surfaces EITHER listener's error.
  const editLoadState = resolveLoadState({
    loading: scheduler.loading || slotsSnapshot.loading,
    error: scheduler.error || slotsSnapshot.error,
  });
  if (isEditing && editLoadState === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState message="Loading session poll..." />
      </div>
    );
  }

  // A failed load surfaces an on-brand error with retry — distinct from the
  // "not found" message below, which an errored (null-data) load would otherwise
  // masquerade as (audit A-4). Must precede the !scheduler.data check.
  if (isEditing && editLoadState === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <ErrorState
          title="Couldn't load this poll"
          message="We hit a problem loading this poll for editing. Check your connection and try again."
          onRetry={() => window.location.reload()}
          className="w-full max-w-md"
        />
      </div>
    );
  }

  if (isEditing && (!scheduler.data || scheduler.data?.status === "ARCHIVED")) {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-600 dark:text-slate-400">
        Session poll not found.
      </div>
    );
  }

  if (isEditing && scheduler.data?.creatorId && scheduler.data.creatorId !== user?.uid) {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-600 dark:text-slate-400">
        Only the creator can edit this poll.
      </div>
    );
  }

  if (isEditing && scheduler.data?.status === "FINALIZED") {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-600 dark:text-slate-400">
        This poll is finalized. Re-open it before editing.
      </div>
    );
  }
  if (isEditing && scheduler.data?.status === "CANCELLED") {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-600 dark:text-slate-400">
        This poll is cancelled. Clone it to propose new times.
      </div>
    );
  }

  return (
    <>
      <form
        onSubmit={handleCreate}
        className="rounded-3xl bg-white p-4 shadow-xl shadow-slate-200 sm:p-8 dark:bg-slate-900 dark:shadow-slate-900/50"
      >
          <SchedulerFormHeader
            title={isEditing ? "Edit Session Poll" : "Create Session Poll"}
            subtitle={
              isEditing
                ? "Update slots and invitees without losing existing votes."
                : "Add a few proposed session slots to kick off voting."
            }
            onBack={() => navigate(isEditing ? `/scheduler/${editId}` : "/dashboard")}
          />

          <div className="mt-6 grid gap-4">
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Session poll title
              <input
                className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                placeholder="Campaign 12 scheduling"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Session poll description
                </span>
                <div className="flex gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setDescriptionTab("write")}
                    className={`rounded-full px-3 py-1 ${
                      descriptionTab === "write"
                        ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                        : "border border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300"
                    }`}
                  >
                    Write
                  </button>
                  <button
                    type="button"
                    onClick={() => setDescriptionTab("preview")}
                    className={`rounded-full px-3 py-1 ${
                      descriptionTab === "preview"
                        ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                        : "border border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300"
                    }`}
                  >
                    Preview
                  </button>
                </div>
              </div>
              {descriptionTab === "write" ? (
                <textarea
                  className="min-h-[96px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  placeholder="Optional details, agenda, or expectations for the session."
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              ) : (
                <PollMarkdownContent
                  content={description}
                  fallback="_No description_"
                  className="min-h-[96px] rounded-2xl border border-slate-200 px-4 py-3 dark:border-slate-700 dark:bg-slate-900"
                />
              )}
            </div>

            <div className="grid gap-2">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Timezone</span>
              <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                <SelectTrigger className="h-12 rounded-2xl px-4">
                  <SelectValue placeholder="Select a timezone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={browserTimezone}>
                    Auto (browser) · {browserTimezone}
                  </SelectItem>
                  {/* The canonical list EXCLUDES the browser timezone — it
                      already renders above as the "Auto (browser)" item. Radix
                      Select keys items by `value`, so a duplicate value (the
                      browser TZ is almost always also in
                      Intl.supportedValuesOf) previously triggered a React "two
                      children with the same key" error on every render. */}
                  {canonicalTimezoneOptions(browserTimezone).map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Questing Group Selector */}
            <QuestingGroupSelect
              groups={groups}
              selectedId={selectedGroupId}
              onChange={(value) => handleGroupChange(value)}
            />

            <InvitePanel
              includedUser={includedUser}
              groupName={selectedGroup?.name || null}
              groupColor={selectedGroup ? getGroupColor(selectedGroup.id) : null}
              groupMembers={groupUsers}
              groupAvatarSize={24}
              inviteUsers={inviteUsers}
              onRemoveInvite={removeInvite}
              pendingInviteUsers={pendingInviteUsers}
              onRemovePendingInvite={removePendingInvite}
              recommendedUsers={recommendedUsers}
              recentInvitees={recentInvitees}
              onAddInvite={addInvite}
              inputValue={inviteInput}
              onInputChange={setInviteInput}
              onAddInput={() => addInvite(inviteInput)}
              error={inviteError}
              onUserClick={handleUserClick}
            />

            <div className="rounded-2xl border border-slate-200/70 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/60">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Anyone with link
                  </p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Allow anyone with the poll URL to join and vote.
                  </p>
                </div>
                <Switch checked={allowLinkSharing} onCheckedChange={setAllowLinkSharing} />
              </div>
              {allowLinkSharing && (
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  Anyone with the link can join after accepting the invite prompt.
                </p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Proposed slots
              </h3>
              <div className="flex items-center gap-3">
                {/* Quick Add is a calendar-surface gesture (tap a day to add your
                    default time; tap again to add another); hide it in the mobile
                    agenda where it would be a no-op dead control. */}
                {(!isMobile || mobileSlotSurface === "calendar") && (
                <label
                  htmlFor="quick-add-toggle"
                  className="flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-xs transition-colors dark:border-slate-600"
                  title="Tap a day to add your default time. Tap it again to add another time. Tap a slot to edit its time, or its × to remove."
                >
                  <Zap
                    className={`h-3 w-3 ${quickAddMode ? "text-amber-500" : "text-slate-400 dark:text-slate-500"}`}
                  />
                  <span className={quickAddMode ? "font-semibold text-amber-600 dark:text-amber-400" : "text-slate-500 dark:text-slate-400"}>
                    Quick Add
                  </span>
                  <Switch
                    id="quick-add-toggle"
                    checked={quickAddMode}
                    onCheckedChange={setQuickAddMode}
                    className="ml-1 scale-75"
                  />
                </label>
                )}
                <button
                  type="button"
                  onClick={() => openModalForDate(new Date())}
                  className="rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold transition-colors hover:bg-slate-50 min-h-[40px] dark:border-slate-600 dark:hover:bg-slate-700"
                >
                  + Add slot
                </button>
              </div>
            </div>
            {/* Phase 4b — friend availability overlay controls. Available
                in both create and edit flows; the projection CF accepts a
                missing pollId as "draft" mode. The picker hides itself
                when there are no eligible candidates. */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <AvailabilityOverlayStrip
                owners={stripOwners}
                onRemove={handleRemoveOverlayOwner}
                colorByOwnerId={colorByOwnerId}
              />
              <AvailabilityOverlayPicker
                candidates={overlayCandidates}
                activeOwnerIds={activeOverlayOwnerIds}
                onToggle={handleToggleOverlayOwner}
                colorByOwnerId={colorByOwnerId}
                onColorChange={handleOverlayColorChange}
                showMyAvailability={showMyAvailability}
                onShowMyAvailabilityChange={handleShowMyAvailabilityChange}
              />
            </div>
            {isMobile && (
              <div
                role="group"
                aria-label="Slot view"
                className="mt-3 flex w-fit gap-2 rounded-full border border-slate-200 bg-white px-2 py-1 dark:border-slate-600 dark:bg-slate-700"
              >
                {[
                  { key: "agenda", label: "Agenda" },
                  { key: "calendar", label: "Calendar" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setMobileSlotSurface(opt.key)}
                    aria-pressed={mobileSlotSurface === opt.key}
                    className={`inline-flex min-h-[44px] items-center justify-center rounded-full px-4 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                      mobileSlotSurface === opt.key
                        ? "bg-brand-primary text-white"
                        : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-600"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            {isMobile && mobileSlotSurface === "agenda" ? (
              <div className="mt-4">
                <CreatorSlotAgenda
                  slots={slots}
                  date={calendarDate}
                  onNavigate={setCalendarDate}
                  effectiveTimezone={effectiveTimezone}
                  invalidSlotIds={invalidSlotIds}
                  calendarEvents={calendarEvents}
                  slotIndicatorsBySlotId={slotIndicatorsBySlotId}
                  tooltipIdentifierByOwnerId={tooltipIdentifierByOwnerId}
                  isEditing={isEditing}
                  slotVotersBySlotId={slotVotersForDisplay}
                  talliesBySlotId={tallies}
                  colorMap={creatorColorMap}
                  onAvatarClick={handleAvatarClick}
                  onAddSlot={() => openModalForDate(new Date())}
                  onAddSlotForDay={(day) => openModalForDate(day)}
                  onEditSlot={openEditSlotModalById}
                  onRemoveSlot={removeSlot}
                />
              </div>
            ) : (
            <div className={`mt-4 rounded-3xl border p-3 transition-colors ${quickAddMode ? "border-amber-300/70 bg-amber-50/40 dark:border-amber-700/50 dark:bg-amber-900/10" : "border-slate-200/70 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60"}`}>
              {/* Below md the month/week grids are unreadable crushed into a
                  ~393px column. Give the grid a comfortable min-width and let
                  the region scroll horizontally (Google-Calendar-on-mobile
                  style) — the toolbar and all RBC + slot selectors stay intact,
                  so this is purely a layout compromise. md+ is a no-op. */}
              <div
                data-testid="create-calendar-scroll"
                className="-mx-1 overflow-x-auto px-1 md:mx-0 md:overflow-x-visible md:px-0"
              >
              {/* Day view is a single column — skip the 44rem min-width so it
                  fits a phone (no pointless horizontal scroll). Month/week keep
                  it for their 7 columns. md+ = no-op. */}
              <div className={`${calendarView === "day" ? "min-w-0" : "min-w-[44rem]"} md:min-w-0`}>
              <DragAndDropCalendar
                key={calendarKey}
                localizer={localizer}
                events={calendarEvents}
                startAccessor="start"
                endAccessor="end"
                selectable="ignoreEvents"
                scrollToTime={scrollToTime}
                enableAutoScroll={calendarView !== "month"}
                date={calendarDate}
                onNavigate={(nextDate) => setCalendarDate(nextDate)}
                view={calendarView}
                onView={(nextView) => setCalendarView(nextView)}
                views={["month", "week", "day"]}
                components={{
                  toolbar: CalendarToolbar,
                  event: eventComponent,
                }}
                tooltipAccessor={(event) =>
                  buildOverlayTooltip({
                    event,
                    identifierByOwnerId: tooltipIdentifierByOwnerId,
                    timeZone: effectiveTimezone,
                  })
                }
                onSelectEvent={(event) => {
                  // Overlay events (friend marks + busy windows) are read-only.
                  const kind = event?.resource?.kind;
                  if (kind === "mark" || kind === "overlay-busy") return;
                  if (quickAddMode) {
                    slotActionsRef.current.openEditSlotModal(event);
                    return;
                  }
                  setSelectedEventId(event.id);
                }}
                onDrillDown={(date) => {
                  if (calendarView !== "month") return;
                  if (quickAddMode) {
                    // In quick add mode, treat date-number clicks the same as cell clicks
                    const safeDate = date instanceof Date ? date : new Date(date);
                    const today = startOfDay(new Date());
                    if (isBefore(startOfDay(safeDate), today)) return;
                    const weekday = getQuickAddWeekday(safeDate);
                    const plan = planQuickAddTap(safeDate, slots, effectiveTimezone, getSessionDefaults(weekday));
                    if (plan.kind === "add") {
                      // Empty day → instant add at the session default.
                      const newSlot = buildQuickAddSlot(safeDate, effectiveTimezone, plan.time, plan.durationMinutes);
                      if (newSlot.start < new Date()) return;
                      setSlots((prev) => [...prev, newSlot]);
                    } else {
                      // Day already has slot(s) → open the picker to add ANOTHER
                      // at a distinct time (pre-filled just after the last slot)
                      // rather than deleting. Removal is via each slot's ×.
                      openModalForDate(safeDate, { time: plan.time, durationMinutes: plan.durationMinutes });
                    }
                  } else {
                    openModalForDate(date);
                  }
                }}
                resizable
                draggableAccessor={(event) => {
                  // Overlay events are read-only — never draggable.
                  const kind = event?.resource?.kind;
                  return kind !== "mark" && kind !== "overlay-busy";
                }}
                onSelectSlot={(slotInfo) => {
                  try {
                    const slotStart = slotInfo.start instanceof Date ? slotInfo.start : new Date(slotInfo.start);

                    if (calendarView === "month") {
                      // Block past days (not including today)
                      const today = startOfDay(new Date());
                      if (isBefore(startOfDay(slotStart), today)) {
                        return; // Silently ignore - visual cues indicate non-interactivity
                      }
                      if (quickAddMode) {
                        const weekday = getQuickAddWeekday(slotStart);
                        const plan = planQuickAddTap(slotStart, slots, effectiveTimezone, getSessionDefaults(weekday));
                        if (plan.kind === "add") {
                          // Empty day → instant add at the session default.
                          const newSlot = buildQuickAddSlot(slotStart, effectiveTimezone, plan.time, plan.durationMinutes);
                          if (newSlot.start < new Date()) {
                            return; // Silently ignore past slots
                          }
                          setSlots((prev) => [...prev, newSlot]);
                        } else {
                          // Day already has slot(s) → open the picker to add ANOTHER
                          // at a distinct time (pre-filled just after the last slot)
                          // rather than deleting. Removal is via each slot's ×.
                          openModalForDate(slotStart, { time: plan.time, durationMinutes: plan.durationMinutes });
                        }
                        return;
                      }
                      openModalForDate(slotInfo.start);
                      return;
                    }
                    // Week/day views - addSlotFromSelection handles past time validation
                    addSlotFromSelection(slotInfo);
                  } catch (err) {
                    console.error("Failed to handle slot selection:", err, slotInfo);
                    toast.error("Unable to add slot. Please try again.");
                  }
                }}
                onEventDrop={({ event, start, end }) => {
                  // Block dropping events to past times
                  if (start < new Date()) {
                    toast.error("Cannot move slot to a past time");
                    return;
                  }
                  updateSlotTimes(event.id, start, end);
                }}
                onEventResize={({ event, start, end }) => {
                  // Block resizing events to start in the past
                  if (start < new Date()) {
                    toast.error("Cannot resize slot to start in the past");
                    return;
                  }
                  updateSlotTimes(event.id, start, end);
                }}
                dayPropGetter={(date) => {
                  if (isBefore(date, startOfDay(new Date()))) {
                    return { className: "rbc-past-day" };
                  }
                  return {};
                }}
                slotPropGetter={(date) => {
                  if (isBefore(date, startOfHour(new Date()))) {
                    return { className: "rbc-past-slot" };
                  }
                  return {};
                }}
                eventPropGetter={(event) => {
                  const isInvalid = invalidSlotIds.has(event.id);
                  const isSelected = selectedEventId === event.id;
                  const r = event?.resource || {};
                  // Phase 4b — render shared-availability mark/busy events
                  // distinctly from the proposed slots. Mark fill comes from
                  // the canonical buildMarkStyleGetter; busy windows render
                  // as a soft striped grey block; per-owner tint adds a 2px
                  // border on top so multiple overlay owners stay visually
                  // distinct without relying on colors that conflict with
                  // the PREFERRED-emerald / FEASIBLE-blue palette.
                  if (r.kind === "mark" || r.kind === "overlay-busy") {
                    const baseStyle =
                      r.kind === "mark"
                        ? buildMarkStyleGetter()(event).style || {}
                        : {
                            backgroundColor: "rgba(148, 163, 184, 0.55)",
                            color: "#1e293b",
                            backgroundImage:
                              "repeating-linear-gradient(45deg, transparent 0 6px, rgba(255,255,255,0.35) 6px 7px)",
                          };
                    // Self overlay events render with NO glow/tint — the
                    // absence of the colorful border is the visual signal
                    // that they're the viewer's own data.
                    if (r.isSelf === true) {
                      return { style: { ...baseStyle, opacity: 0.85 } };
                    }
                    const resolvedColor =
                      colorByOwnerId[event?.resource?.ownerId] || null;
                    const tintStyle = ownerTintStyle(event, resolvedColor);
                    // Soft outer glow keyed to the same color so events read
                    // visually-grouped by owner without relying on the 2px
                    // border alone (matches the chip glow in the strip).
                    const glowStyle = resolvedColor
                      ? {
                          boxShadow: `0 0 0 1px ${resolvedColor}, 0 0 6px 0 ${resolvedColor}55`,
                        }
                      : {};
                    return {
                      style: {
                        ...baseStyle,
                        ...tintStyle,
                        ...glowStyle,
                        opacity: 0.85,
                      },
                    };
                  }
                  if (!isInvalid && !isSelected) return {};
                  return {
                    style: {
                      ...(isInvalid
                        ? { backgroundColor: "#dc2626", borderColor: "#b91c1c" }
                        : {}),
                      ...(isSelected
                        ? {
                            boxShadow:
                              "0 0 0 2px rgba(59, 130, 246, 0.7), 0 0 12px rgba(59, 130, 246, 0.35)",
                          }
                        : {}),
                    },
                  };
                }}
                style={{ height: 630 }}
              />
              </div>
              </div>
              <div className="mt-3 flex justify-end">
                <CalendarJumpControls
                  hasEvents={hasEvents}
                  hasEventsInView={hasEventsInView}
                  onPrev={jumpPrev}
                  onNext={jumpNext}
                  onPrevWindow={jumpPrevWindow}
                  onNextWindow={jumpNextWindow}
                  label="Jump to slot"
                />
              </div>
            </div>
            )}
            {/* Calendar-surface usage hint — hidden in the mobile agenda where
                these grid gestures don't apply. */}
            {(!isMobile || mobileSlotSurface === "calendar") && (
            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
              {quickAddMode
                ? "Quick Add: tap a day to add your default time · tap it again to add another · tap a slot's \u00d7 to remove · tap a slot to edit its time."
                : "Month view opens a modal. Week/day views add slots instantly and support drag/resize."}
            </p>
            )}
            {/* The flat "Proposed slots" list (with its edit-mode vote tallies)
                is the desktop slot surface; on mobile the agenda above already
                lists every slot with Edit/Remove, so render this list only on
                desktop or the mobile Calendar surface — otherwise the same slots
                appear twice on a phone. */}
            {(!isMobile || mobileSlotSurface === "calendar") && (
            <div className="mt-4 space-y-2">
              {slots.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No slots added yet. Click on the calendar to add one.
                </p>
              )}
              {slots.map((slot) => (
                <div
                  key={slot.id}
                  className={`flex items-center justify-between rounded-2xl border px-4 py-3 dark:bg-slate-900 ${
                    invalidSlotIds.has(slot.id)
                      ? "border-red-300 bg-red-50/60 dark:border-red-700 dark:bg-red-900/20"
                      : "border-slate-200/70 bg-white dark:border-slate-700"
                  }`}
                >
                  <div>
                    <p
                      className="flex items-center text-sm font-semibold text-slate-900 dark:text-slate-100"
                      data-testid={`slot-card-headline-${slot.id}`}
                    >
                      <span>
                        {formatZonedDateTime(
                          slot.start,
                          effectiveTimezone,
                          "MMM d, yyyy · h:mm a",
                          { showTimeZone }
                        )}
                      </span>
                      <SlotOverlayIndicators
                        conflict={slotIndicatorsBySlotId[slot.id]?.conflict || []}
                        preferred={slotIndicatorsBySlotId[slot.id]?.preferred || []}
                        feasible={slotIndicatorsBySlotId[slot.id]?.feasible || []}
                        identifierByOwnerId={tooltipIdentifierByOwnerId}
                      />
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Duration {Math.round((slot.end - slot.start) / 60000)} min
                    </p>
                    {isEditing && (
                      <div className="mt-2 flex flex-col gap-2">
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                          <span className="font-semibold">★ Preferred</span>
                          <AvatarStack
                            users={(slotVotersForDisplay[slot.id] || {}).preferred || []}
                            max={6}
                            size={20}
                            colorMap={creatorColorMap}
                            onClick={handleAvatarClick}
                          />
                          <span className="text-slate-400 dark:text-slate-500">
                            {(tallies[slot.id]?.preferred || 0)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                          <span className="font-semibold">✓ Feasible</span>
                          <AvatarStack
                            users={(slotVotersForDisplay[slot.id] || {}).feasible || []}
                            max={6}
                            size={20}
                            colorMap={creatorColorMap}
                            onClick={handleAvatarClick}
                          />
                          <span className="text-slate-400 dark:text-slate-500">
                            {(tallies[slot.id]?.feasible || 0)}
                          </span>
                        </div>
                      </div>
                    )}
                    {invalidSlotIds.has(slot.id) && (
                      <p className="mt-2 text-xs font-semibold text-red-500 dark:text-red-400">
                        This slot is in the past. Remove it to save.
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSlot(slot.id)}
                    className="min-h-[40px] rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500 transition-colors hover:bg-red-50 hover:border-red-200 hover:text-red-600 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-red-900/30 dark:hover:border-red-800 dark:hover:text-red-300"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            )}
          </div>

          <SessionVotingDeadlineField
            value={votingDeadlineLocal}
            onChange={setVotingDeadlineLocal}
            timeZone={effectiveTimezone}
            slots={slots}
            error={votingDeadlineValidation.error}
            warning={votingDeadlineValidation.warning}
            disabled={submitting}
          />

          <div className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Add-on polls
                </h3>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {isEditing
                    ? "Add optional or required polls for participants to complete."
                    : "Add optional or required polls now. They are saved when you create the session poll."}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {pollImportEnabled ? (
                  <button
                    type="button"
                    aria-label="Import add-on polls"
                    title="Import add-on polls"
                    onClick={() => setAddonImportOpen(true)}
                    className="rounded-full border border-slate-200 p-1.5 text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    <Upload className="h-3.5 w-3.5" />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={openAddEmbeddedPoll}
                  className="rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold transition-colors hover:bg-slate-50 min-h-[40px] dark:border-slate-600 dark:hover:bg-slate-700"
                >
                  + Add poll
                </button>
              </div>
            </div>

            {displayedEmbeddedPolls.length > 5 ? (
              <p className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200">
                This session has many add-on polls. Consider keeping it to 1-5 to reduce voter fatigue.
              </p>
            ) : null}

            <div className="mt-4 space-y-3">
              {isEditing && embeddedPollsLoading ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">Loading add-on polls...</p>
              ) : null}
              {(!isEditing || !embeddedPollsLoading) && displayedEmbeddedPolls.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  No add-on polls yet.
                </p>
              ) : null}
              {displayedEmbeddedPolls.length > 0 ? (
                <DndContext
                  sensors={embeddedPollSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleEmbeddedPollDragEnd}
                >
                  <SortableContext
                    items={displayedEmbeddedPolls.map((poll) => poll.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {displayedEmbeddedPolls.map((poll) => (
                        <SortableEmbeddedPollCard
                          key={poll.id}
                          poll={poll}
                          voteCount={embeddedPollVoteCounts[poll.id] || 0}
                          participantCount={embeddedPollParticipantCount}
                          onEdit={() => openEditEmbeddedPoll(poll)}
                          onRemove={() => confirmDeleteEmbeddedPoll(poll)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : null}
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200/70 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/60">
            <VotePrivacySettings
              expanded={advancedSettingsExpanded}
              onExpandedChange={setAdvancedSettingsExpanded}
              calendarSyncEnabled={calendarSyncEnabled}
              onCalendarSyncEnabledChange={setCalendarSyncEnabled}
              voteVisibility={voteVisibility}
              onVoteVisibilityChange={(nextVisibility) => {
                setVoteVisibility(nextVisibility);
                setHideVoterIdentities((previous) =>
                  resolveHideVoterIdentitiesForVisibility(previous, nextVisibility)
                );
              }}
              hideVoterIdentities={hideVoterIdentities}
              onHideVoterIdentitiesChange={setHideVoterIdentities}
              voteAnonymization={voteAnonymization}
              onVoteAnonymizationChange={setVoteAnonymization}
              voteAnonymizationLocked={voteAnonymizationLocked}
              summaryItems={[
                pollReminderSummary(
                  reminderSettings,
                  inheritedReminderSettings,
                  "session"
                ),
                autoNagSummary(autoNag),
              ]}
            >
              <div className="space-y-3">
                <PollNotificationSettings
                  idPrefix="session-poll"
                  group={selectedGroup}
                  value={reminderSettings}
                  inheritedValue={inheritedReminderSettings}
                  onChange={setReminderSettings}
                  scope="session"
                />
                <AutoNagSettings
                  group={selectedGroup}
                  value={autoNag}
                  onChange={setAutoNag}
                  timeZone={effectiveTimezone}
                  votingDeadlineAt={votingDeadlineValidation.deadlineAt}
                  earliestSlotAt={earliestProposedSlotAt}
                  nagState={scheduler.data?.nagState}
                />
              </div>
            </VotePrivacySettings>
          </div>

          {hasInvalidSlots && (
            <p className="mt-4 text-sm text-red-500 dark:text-red-400">
              Remove past slots before saving changes.
            </p>
          )}

          {createdId && (
            <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-900/30 dark:text-emerald-200">
              Session poll created. Share link: {`${APP_URL}/scheduler/${createdId}`}
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-brand-primary px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:opacity-50"
            >
              {submitting
                ? isEditing
                  ? "Saving..."
                  : "Creating..."
                : isEditing
                  ? "Update poll"
                  : "Create poll"}
            </button>
          </div>
      </form>
      <Dialog
        open={calendarUpdateOpen}
        onOpenChange={(open) => {
          setCalendarUpdateOpen(open);
          if (open) {
            setCalendarUpdateChecked(false);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Google Calendar entry</DialogTitle>
            <DialogDescription>
              This poll has an existing calendar event. Confirm if it should be updated before saving changes.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 rounded-2xl border border-slate-200/70 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={calendarUpdateChecked}
                onChange={(event) => setCalendarUpdateChecked(event.target.checked)}
              />
              Yes, update Google Calendar entry (delete the linked event)
            </label>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              You can create a new event again when the poll is finalized.
            </p>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setCalendarUpdateOpen(false)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmEditSave}
              disabled={submitting}
              className="rounded-full bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:opacity-50"
            >
              {submitting ? "Saving..." : "Continue"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={modalOpen} onOpenChange={(open) => { setModalOpen(open); if (!open) setEditingSlotId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSlotId ? "Edit slot" : "Add a slot"}</DialogTitle>
            <DialogDescription>
              Choose a date and time in {effectiveTimezone}.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 grid gap-3">
            <div className="grid gap-1">
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                Date
              </span>
              <DatePicker
                date={draftDate}
                onSelect={setDraftDate}
                placeholder="Select a date"
              />
            </div>
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">
              Start time
              <input
                type="time"
                value={draftTime}
                onChange={(event) => setDraftTime(event.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
            </label>
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400">
              Duration (min)
              <input
                type="number"
                min="30"
                step="30"
                value={draftDuration}
                onChange={(event) => setDraftDuration(event.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
            </label>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveDraftSlot}
              className="rounded-full bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90"
            >
              {editingSlotId ? "Save changes" : "Add slot"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <EmbeddedPollEditorModal
        open={embeddedPollEditorOpen}
        onOpenChange={(nextOpen) => {
          setEmbeddedPollEditorOpen(nextOpen);
          if (!nextOpen) setEditingEmbeddedPoll(null);
        }}
        initialPoll={editingEmbeddedPoll}
        isPersistedEdit={isEditing && Boolean(editingEmbeddedPoll?.id)}
        votedCount={
          editingEmbeddedPoll
            ? embeddedPollVoteMeta[editingEmbeddedPoll.id]?.count ?? 0
            : 0
        }
        votesKnown={
          editingEmbeddedPoll
            ? embeddedPollVoteMeta[editingEmbeddedPoll.id]?.known === true
            : false
        }
        onSave={handleSaveEmbeddedPoll}
        saving={embeddedPollSaveBusy}
        group={selectedGroup}
        inheritedReminderSettings={reminderSettings}
      />
      {pollImportEnabled ? (
        <UploadPollJsonDialog
          open={addonImportOpen}
          onOpenChange={setAddonImportOpen}
          mode={IMPORT_MODE.ADDON}
          onAddonImport={handleAddonImport}
        />
      ) : null}
      <Dialog
        open={deleteEmbeddedPollOpen}
        onOpenChange={(nextOpen) => {
          setDeleteEmbeddedPollOpen(nextOpen);
          if (!nextOpen) setEmbeddedPollToDelete(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove add-on poll</DialogTitle>
            <DialogDescription>
              Remove "{embeddedPollToDelete?.title || "this poll"}"? All add-on poll votes will be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => {
                setDeleteEmbeddedPollOpen(false);
                setEmbeddedPollToDelete(null);
              }}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteEmbeddedPoll}
              disabled={embeddedPollDeleteBusy}
              className="rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-500 disabled:opacity-60"
            >
              {embeddedPollDeleteBusy ? "Removing..." : "Remove poll"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ProfileInteractionModals {...modalProps} />
    </>
  );
}
